/**
 * U0 · **both axes** of a step, in one form — laid out as the mock-up lays it out.
 *
 * Five questions, not one wall (the mock's own words). Each is a section with the mock's
 * label, folded or open by default according to the mode, and badged with what it holds so
 * nothing is hidden by being closed:
 *
 *   What happens              what the operator does · step type · performed by · signature
 *                             · who may give it · stage
 *   What the operator sees    instruction · checklist · records a parameter · CPP → CQA
 *   How it runs here          repetition and concurrency from the operation; controls
 *   Rules bound here          the rules this step binds — provenance, scope, tightening
 *   Where it came from        stable code · authored from · bound rules · free values
 *
 * The same component edits a BMR step and a sub-operation template. The only differences are
 * inputs, not code (U0 §2): `blastRadius`, `showRole`, `readOnly`.
 *
 * Two fields the mock has and the model does not yet — `stage` on a step, and a checklist
 * binding — are shown as exactly that. Drawing a working control over a field that is not
 * there would be the surface inventing a fact. Binding a rule from here needs an endpoint the
 * API does not have; the bound rules are shown, the control is not drawn (U0 §3).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Cascader, Dropdown, Flex, Input, InputNumber, Modal, Popconfirm, Radio, Select, Space, Switch, TimePicker, Tooltip, Typography, message } from "antd";
import dayjs from "dayjs";
import { useFieldPrefs } from "./fieldPrefs";

/** The share this step had when last saved — the server's remaining already excludes it. */
const props_saved_share = (step) => {
  const saved = step?.__saved_charge;
  return saved != null && saved !== "" ? Number(saved) : 0;
};

/** "0.034000" → "0.034": a quantity as a person writes it. */
const fmtQty = (v) => (v == null || v === "" ? "" : String(Number(v)));
import { DeleteOutlined } from "@ant-design/icons";
import { Eye, FileText, History, Info, Layers, Plus, Repeat, ShieldCheck } from "lucide-react";

import CriticalityEdges from "./CriticalityEdges";
import DecisionForm from "./DecisionForm";
import StepAttachments from "./StepAttachments";
import { Boxes, FlaskConical, Gauge, ListPlus, Paperclip } from "lucide-react";
import LoopForm from "./LoopForm";
// 2026-10-07 · each step type's own form lives in its own file under ./steps.
import { Field, HelpContext, HelpText, useHelp } from "./steps/Field";
import { chipState, cpvOffPatch, cpvOn, removeSecondPatch, typeWords } from "./stepPanelModel";
import { EquipmentPicker } from "./steps/machines";
import { INGREDIENT_STEP_TYPES, IngredientSettings } from "./steps/IngredientForm";
import { FormulaField, QuantityFromField, VerifyArField } from "./steps/FormulaField";
import DependsOnField from "./steps/DependsOnField";
import LinkedToField from "./steps/LinkedToField";
import InspectionPassField, { passByItself } from "./steps/InspectionPassField";
/** The operation type whose Sample step may be the AQL sample. The In-Process Check link below does not read it: the plan and the step decide. */
const IPC_OPERATION_TYPE_CODE = "OT-VI-100";
/** The step types made for the AQL sample (the vocabulary's AQL_SAMPLE_TYPES): an AQL sample by the step's type, in a unit-count operation of any code. */
const AQL_SAMPLE_STEP_TYPES = ["AQL_SAMPLE"];
/** A Sample step of a visual-inspection operation: its sample, tubs and limits come from the AQL section, not from a lab sample's department / test type. */
const isSampleOfVi = (step, operation) => !!(step && step.kind === "STEP" && operation && !step.span && step.step_type === "SAMPLE"
  && operation.inspection?.behaviour === "UNIT_COUNT" && operation.type_ref?.code === IPC_OPERATION_TYPE_CODE);
/** A step of a type made for the AQL sample, at the top level of a unit-count operation — one of the operation's own children, not inside a loop or
 *  a lane (services/recipe/aql_setup.py `_is_aql_sample_type`). */
const isAqlSampleTypeStep = (step, operation) => !!(step && step.kind === "STEP" && operation && !step.span && AQL_SAMPLE_STEP_TYPES.includes(step.step_type)
  && operation.inspection?.behaviour === "UNIT_COUNT" && (operation.children || []).some((c) => c.code === step.code));
/** An AQL sample step, by either path: the Sample step of the visual-inspection operation, or a step of a type made for the AQL sample. */
const isAqlSampleStep = (step, operation) => isSampleOfVi(step, operation) || isAqlSampleTypeStep(step, operation);
import { CleaningField } from "./steps/CleaningField";
import { AttachmentField } from "./steps/AttachmentField";
import { MON_DEFAULTS, MON_OWNED_FACTS, MonitoringEditor, MonitoringRules, monitoringOf, monitoringSentence } from "./steps/MonitoringForm";
import { SamplingSettings } from "./steps/SamplingForm";
import AqlSetupField from "./steps/AqlSetupField";
import ProvenanceBadge from "./ProvenanceBadge";
import { factsOf, familyOf } from "./vocabulary";

const { Text } = Typography;

const SECOND = ["CHECKED_BY", "VERIFIED_BY", "WITNESSED_BY"];
const SIGN_TEXT = { CHECKED_BY: "Checked By", VERIFIED_BY: "Verified By", WITNESSED_BY: "Witnessed By" };
// 2026-10-29 (owner) · extra signatures on one step, the first included — the plant's `sign_max` (vocabulary,
// set on Recipe Building Blocks → Signatures) when it has one, else this (backend second_signer.MAX_EXTRA)
const MAX_SIGNERS = 10;
// 2026-10-29 (owner) · the people's labels with a capital "By": "Performed By", "Checked By", "Clearance Given By"
const capBy = (t) => String(t || "").replace(/\b[a-z]/g, (c) => c.toUpperCase());

/** 2026-10-29 (owner) · the E-sign switch beside each person: on — signs with username and password;
 *  off — confirms as the signed-in user (role and different-person still checked). */
function EsignSwitch({ on, onChange, readOnly, who }) {
  return (
    <Tooltip title={on ? `${who} signs with an e-signature (username and password)` : `${who} confirms without an e-signature`}>
      <label className={`dz-esign ${on ? "on" : ""}`}>
        <Switch size="small" checked={!!on} disabled={readOnly} onChange={onChange} aria-label={`E-signature for ${who}`} />
        <span>E-sign</span>
      </label>
    </Tooltip>
  );
}
// RV9y · the signature is one dropdown of LABELS ("Done by", "Clearance given by"); each label
// is bound to the closed meaning the engine evaluates. The meaning of a draft is read through
// the label it picked, so "who may give it" appears the moment a second-person label is chosen.
const meaningOf = (step, labels = []) => {
  if (step.sign_label !== undefined) {
    if (!step.sign_label || step.sign_label === "NONE") return "NONE";
    return labels.find((l) => l.value === step.sign_label)?.meaning || step.signature_meaning || "NONE";
  }
  return step.signature_meaning || "NONE";
};
const labelOf = (step, labels = []) => {
  if (step.sign_label !== undefined) return step.sign_label || "NONE";
  if (step.signature) return labels.some((l) => l.value === step.signature) ? step.signature : (labels.find((l) => l.meaning === step.signature_meaning)?.value || "NONE");
  return "NONE";
};


/** The rail facet for a step — one, because the five questions live inside it. */
// A step's role is optional: who performs the work is set on the operation ("Performed by"),
// so a step without one of its own is not a gap.
export function stepFacets(step) {
  if (!step) return [];
  // A structure is not a step. Its one facet asks the structure's own question.
  if (step.kind === "LOOP") {
    return [{ key: "step", icon: Repeat, label: "The repeat — what, and at the merge",
              badge: !step.payload?.over, tone: "block" }];
  }
  if (step.kind === "DECISION") {
    return [{ key: "step", icon: Layers, label: "The decision — what it depends on, its lanes", badge: !step.title || !step.depends_on, tone: "block" }];
  }
  if (step.kind === "LANE") {
    return [{ key: "step", icon: Layers, label: "The lane — its condition", badge: !step.label, tone: "block" }];
  }
  const second = SECOND.includes(meaningOf(step));
  const gap = (second && !(step.signed_by_id ?? step.signature_role_id) && !step.signature_policy) || !step.title;
  return [{ key: "step", icon: Layers, label: "The step", badge: gap, tone: "block" }];
}

const SECTION_ICON = { basic: FileText, does: Eye, ingredient: Boxes, docs: Paperclip, runs: Repeat, rules: ShieldCheck, trace: History, details: ListPlus, instruction: FileText, monitoring: Gauge, sampling: FlaskConical, order: Layers };


/** 2026-10-14 (owner) · a section's explanation, behind an ⓘ at the right end of its heading —
 *  read on hover, never on the page. Replaces the panel's "Show help" switch. */
export function InfoTip({ text, label = "About this" }) {
  if (!text) return null;
  return (
    <Tooltip title={text} placement="topRight">
      <span className="u0-info" role="img" aria-label={label} tabIndex={0} onClick={(e) => e.stopPropagation()}>
        <Info size={14} strokeWidth={1.9} />
      </span>
    </Tooltip>
  );
}

/** 2026-10-14 (owner) · a flat, always-open section of a side panel drawn like the step panel's:
 *  small-capital heading, ⓘ at the right end. Used by the operation panel. */
export function PanelSection({ title, info = null, children, className = "" }) {
  return (
    <section className={`u0-sec open ${className}`}>
      <div className="u0-sechrow">
        <div className="u0-sech"><span className="t">{title}</span></div>
        <InfoTip text={info} label={`About ${title}`} />
      </div>
      <div className="u0-secb">{children}</div>
    </section>
  );
}

function Section({ id, title, badge, tone, open, onToggle, children, help = null, adds = [], className = "" }) {
  const Icon = SECTION_ICON[id] || FileText;
  // 2026-10-14 (step panel redesign, option A) · what can be added to this group, behind a "+" in
  // its heading — nothing extra on the page at rest.
  const items = (adds || []).filter(Boolean);
  return (
    <section className={`u0-sec ${open ? "open" : ""} ${className}`}>
      <div className="u0-sechrow">
        <button type="button" className="u0-sech" aria-expanded={open} data-sec={id}
                onClick={() => onToggle(id)}>
          <span className="ic"><Icon size={14} strokeWidth={1.8} /></span>
          <span className="t">{title}</span>
          {badge ? <span className={`n ${tone || ""}`}>{badge}</span> : null}
          <span className="cv">▾</span>
        </button>
        {items.length ? (
          <Dropdown trigger={["click"]} menu={{ items: items.map((a) => ({ key: a.key, label: a.label, onClick: a.onClick })) }}>
            <button type="button" className="u0-secadd" aria-label={`Add to ${title}`} onClick={(e) => e.stopPropagation()}><Plus size={15} strokeWidth={1.9} /></button>
          </Dropdown>
        ) : null}
        <InfoTip text={help} label={`About ${title}`} />
      </div>
      {open ? <div className="u0-secb">{children}</div> : null}
    </section>
  );
}

/** Seconds → "N min" / "N h M min" for a rule's duration limit. */
function mins(seconds) {
  if (seconds == null) return null;
  const m = Math.round(Number(seconds) / 60);
  return m >= 60 && m % 60 === 0 ? `${m / 60} h` : m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
}
/** Minutes → the "HH:MM:SS" the server's duration field reads. */
function hhmmss(minutes) {
  if (minutes == null || minutes === "") return null;
  const m = Math.max(0, Math.round(Number(minutes)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}:00`;
}
/** A timed rule reads in words: a CADENCE's max is "at least every N", its min "no sooner than N";
 *  a CLOCK's window is "between"; for the eye-rest shape (max = continuous work before rest,
 *  min = the rest itself) both are said. */
function durationText(rule) {
  const p = rule.params || {};
  const lo = mins(p.min_duration), hi = mins(p.max_duration);
  if (rule.rule_type === "CADENCE" && rule.scope === "PERSON") {
    const day = p.daily_max_min != null ? `≤ ${p.daily_max_min} min per day` : null;
    return [hi ? `≤ ${hi} continuous` : null, lo ? `rest ≥ ${lo}` : null, day].filter(Boolean).join(" · ");
  }
  if (rule.rule_type === "CADENCE") return [hi ? `at least every ${hi}` : null, lo ? `not before ${lo}` : null].filter(Boolean).join(" · ");
  return [lo ? `≥ ${lo}` : null, hi ? `≤ ${hi}` : null].filter(Boolean).join(" · ");
}
const TIMED = new Set(["CADENCE", "CLOCK"]);

/** One bound rule: type · name · limit · provenance; tighten (never widen) and unbind. */
function RuleRow({ rule, readOnly, onTighten, onUnbind, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [lo, setLo] = useState(rule.min_value ?? null);
  const [hi, setHi] = useState(rule.max_value ?? null);
  const p = rule.params || {};
  const [loMin, setLoMin] = useState(p.min_duration != null ? Math.round(p.min_duration / 60) : null);
  const [hiMin, setHiMin] = useState(p.max_duration != null ? Math.round(p.max_duration / 60) : null);
  const [dayMin, setDayMin] = useState(p.daily_max_min != null ? Number(p.daily_max_min) : null);
  const timed = TIMED.has(rule.rule_type);
  const limit = timed ? durationText(rule)
    : [rule.min_value != null ? `≥ ${rule.min_value}` : null, rule.max_value != null ? `≤ ${rule.max_value}` : null]
      .filter(Boolean).join(" · ") + (rule.unit ? ` ${rule.unit}` : "");
  const hasLimit = timed ? (p.min_duration != null || p.max_duration != null) : (rule.min_value != null || rule.max_value != null);
  const person = rule.rule_type === "CADENCE" && rule.scope === "PERSON";
  const pendingWord = { tighten: "tightened — not saved", policy: "from policy — not saved", unbind: "unbind — not saved", bind: "new — not saved" }[rule.pending];
  return (
    <div className={`u0-rule ${rule.pending ? `pending pending-${rule.pending}` : ""}`} data-testid="rule-row">
      <span className="rt">{rule.rule_type}</span>
      <span className="rn">{rule.name}</span>
      {pendingWord ? <span className="dz-chip warn" data-testid="rule-pending">{pendingWord}</span> : null}
      {limit ? <span className="rl">{limit}</span> : timed ? <span className="rl" style={{ color: "var(--dz-warn)" }}>no time set — the plant policy applies</span> : null}
      <ProvenanceBadge value={rule.provenance} sourceRef={rule.source_ref} compact />
      {rule.inherited_from ? <span className="dz-chip" title="Bound from the library; may be tightened here, never widened">inherited</span> : null}
      {rule.policy ? (
        rule.policy.differs ? (
          <span className="dz-chip warn" data-testid="policy-differs"
                title={`The plant's ${rule.policy.key} policy says ${mins(rule.policy.policy.max_duration) || "—"} continuous · rest ${mins(rule.policy.policy.min_duration) || "—"}; this rule says otherwise.`}>
            policy: {mins(rule.policy.policy.max_duration) || "—"} · rest {mins(rule.policy.policy.min_duration) || "—"}
          </span>
        ) : <span className="dz-chip ok" title={`Matches the plant's ${rule.policy.key} policy`}>= policy</span>
      ) : null}
      {rule.policy?.differs && !readOnly && onRefresh && !rule.pending ? (
        <Button size="small" type="link" data-testid="refresh-rule" onClick={() => onRefresh(rule.id)}>Update from policy</Button>
      ) : null}
      {!readOnly && onTighten && (hasLimit || timed) && rule.pending !== "unbind" && rule.pending !== "bind" ? (
        <Button size="small" type="link" onClick={() => setEditing((v) => !v)}>{editing ? "Cancel" : hasLimit ? "Tighten" : "Set"}</Button>
      ) : null}
      {!readOnly && onUnbind && !rule.pending ? (
        <Popconfirm title="Unbind this rule?" description="The step will carry no limit from it. A library rule stays in the library."
                    okText="Unbind" onConfirm={() => onUnbind(rule.id)}>
          <Button size="small" type="link" danger>✕</Button>
        </Popconfirm>
      ) : null}
      {editing && timed ? (
        <div className="u0-tighten" data-testid="rule-tighten">
          <span className="fk">{person ? "continuous, max (min)" : "max (min)"}</span><InputNumber size="small" min={0} value={hiMin} onChange={setHiMin} style={{ width: 90 }} />
          <span className="fk">{person ? "rest, min (min)" : "min (min)"}</span><InputNumber size="small" min={0} value={loMin} onChange={setLoMin} style={{ width: 90 }} />
          {person ? <><span className="fk">per day, max (min)</span><InputNumber size="small" min={0} value={dayMin} onChange={setDayMin} style={{ width: 90 }} /></> : null}
          <Button size="small" type="primary" onClick={() => { onTighten(rule.id, { max_duration: hhmmss(hiMin), min_duration: hhmmss(loMin), ...(person ? { params: { daily_max_min: dayMin } } : {}) }); setEditing(false); }}>Apply</Button>
          {rule.inherited_from ? <span className="u0-tighten-note">The library's limit is the ceiling: a value outside it is refused.</span>
            : <span className="u0-tighten-note">The plant's policy is the ceiling once the Clocks module lands; today the record states it.</span>}
        </div>
      ) : editing ? (
        <div className="u0-tighten">
          <span className="fk">min</span><InputNumber size="small" value={lo} onChange={setLo} style={{ width: 90 }} />
          <span className="fk">max</span><InputNumber size="small" value={hi} onChange={setHi} style={{ width: 90 }} />
          <Button size="small" type="primary" onClick={() => { onTighten(rule.id, { min_value: lo, max_value: hi }); setEditing(false); }}>Apply</Button>
          {rule.inherited_from ? <span className="u0-tighten-note">The library's limit is the ceiling: a value outside it is refused.</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** "+ bind a rule": the library, grouped by template, or a rule authored here. */
/**
 * 2026-10-08 · how long the step takes — the same controls the operation has on the process
 * configuration page: Duration required, Min / Max (HH:MM) and a Deviation rule. Not a field of the
 * step (a step never owns a limit) but a CLOCK rule bound to it, "Step duration", so the runner
 * times it like any timed rule (acts.clock_of: start, refused stop before the minimum, deviation
 * outside the window).
 *
 * The deviation rule decides which side is ENFORCED. Only an enforced side is written as the rule's
 * limit (min_duration / max_duration — what the runner reads); the other is kept in `params` as the
 * expected time, for the record. "None": both are expected times only, so nothing is timed or raised.
 * Set / change / remove are staged like every rule edit and written by the form's Save.
 */
const DURATION_RULE = "Step duration";
const DEVIATION_RULES = [
  { value: "none", label: "None" },
  { value: "less_than_min", label: "Less than Min" },
  { value: "greater_than_max", label: "Greater than Max" },
  { value: "less_than_min_or_greater_than_max", label: "Less than Min or Greater than Max" },
];
const enforcesMin = (rule) => rule === "less_than_min" || rule === "less_than_min_or_greater_than_max";
const enforcesMax = (rule) => rule === "greater_than_max" || rule === "less_than_min_or_greater_than_max";
const secToHHMM = (s) => (s == null ? null : `${String(Math.floor(Number(s) / 3600)).padStart(2, "0")}:${String(Math.round((Number(s) % 3600) / 60)).padStart(2, "0")}`);
const hhmmToSec = (t) => { if (!t) return null; const [h, m] = String(t).split(":").map(Number); return (h || 0) * 3600 + (m || 0) * 60; };

function StepDuration({ bound, readOnly, onBind, onTighten, onUnbind }) {
  const rule = bound.find((r) => r.rule_type === "CLOCK" && r.name === DURATION_RULE && !r.inherited_from && r.pending !== "unbind");
  const p = rule?.params || {};
  const saved = {
    on: !!rule,
    min: secToHHMM(p.expected_min ?? p.min_duration),
    max: secToHHMM(p.expected_max ?? p.max_duration),
    dev: p.deviation_rule || (rule ? "less_than_min_or_greater_than_max" : "less_than_min_or_greater_than_max"),
    // 2026-10-12 · where the time is counted from (auto_clock.py): the operator's Start, or the
    // moment the step before it was completed — "read within 2 minutes of the end of the hold".
    from: p.anchor === "PREVIOUS_END" ? "PREVIOUS_END" : "STEP_START",
  };
  const [on, setOn] = useState(saved.on);
  const [min, setMin] = useState(saved.min);
  const [max, setMax] = useState(saved.max);
  const [dev, setDev] = useState(saved.dev);
  const [from, setFrom] = useState(saved.from);
  const [ref, setRef] = useState("");
  useEffect(() => { setOn(saved.on); setMin(saved.min); setMax(saved.max); setDev(saved.dev); setFrom(saved.from); },
    // a different step, or the saved rule changed underneath
    [rule?.id, p.expected_min, p.expected_max, p.min_duration, p.max_duration, p.deviation_rule, p.anchor]); // eslint-disable-line react-hooks/exhaustive-deps
  const minS = hhmmToSec(min), maxS = hhmmToSec(max);
  const error = minS != null && maxS != null && minS > maxS ? "Min is longer than Max."
    : enforcesMin(dev) && !minS ? "This deviation rule needs a Min duration."
    : enforcesMax(dev) && !maxS ? "This deviation rule needs a Max duration."
    : !minS && !maxS ? "Give a Min or a Max duration." : null;
  const changed = on !== saved.on || min !== saved.min || max !== saved.max || dev !== saved.dev || from !== saved.from;
  const persisted = rule && !String(rule.id).startsWith("pending-");
  const canApply = !readOnly && on && changed && !error;
  const body = () => {
    const src = ref.trim() || rule?.source_ref || "Entered on the master record";
    return {
      rule_type: "CLOCK", name: DURATION_RULE, provenance: "CITED", source_ref: src,
      min_duration: enforcesMin(dev) && minS ? `${min}:00` : null,
      max_duration: enforcesMax(dev) && maxS ? `${max}:00` : null,
      params: { deviation_rule: dev, ...(minS != null ? { expected_min: minS } : {}), ...(maxS != null ? { expected_max: maxS } : {}),
                ...(from === "PREVIOUS_END" ? { anchor: "PREVIOUS_END" } : {}) },
    };
  };
  const preview = (b) => ({ rule_type: "CLOCK", name: DURATION_RULE, provenance: "CITED", source_ref: b.source_ref,
    params: { ...b.params, min_duration: hhmmToSec(b.min_duration), max_duration: hhmmToSec(b.max_duration) } });
  const apply = () => {
    const b = body();
    // A change rewrites the rule (its enforced side may move with the deviation rule): the old
    // binding is unbound and the new one bound, in one Save.
    if (persisted) onUnbind?.(rule.id);
    onBind?.(b, preview(b));
  };
  const toggle = (v) => {
    setOn(v);
    if (!v && persisted) onUnbind?.(rule.id);      // switching it off removes the duration on Save
  };
  if (readOnly && !rule) return null;
  const tp = (value, set, label) => (
    <div style={{ flex: "1 1 110px", minWidth: 0 }}>
      <div className="l" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))", fontWeight: 600, color: "var(--dz-field-label)", marginBottom: 2 }}>{label}</div>
      <TimePicker format="HH:mm" size="small" style={{ width: "100%" }} disabled={readOnly} aria-label={label}
                  value={value ? dayjs(value, "HH:mm") : null} placeholder="HH:MM" showNow={false} needConfirm={false}
                  onChange={(t, str) => set(str || null)} />
    </div>
  );
  return (
    // A div, not <Field> (a <label>): several controls live here, and a label would hand every
    // click inside it to the first one — the Duration required switch.
    // 2026-10-14 (owner) · "Duration" with its Duration-required switch on the right of the same
    // row; everything else full width under it.
    <div data-testid="step-duration" style={{ width: "100%" }}>
      <Flex align="center" justify="space-between" gap={8} wrap="wrap" style={{ marginBottom: on ? 6 : 0 }}>
        <span className="l" style={{ fontSize: "calc(13px * var(--dz-fs, 1))", fontWeight: 600, color: "var(--dz-field-label)" }}>Duration</span>
        <Space size={8} align="center">
          {rule?.pending ? <span className="dz-chip warn">not saved — press Save</span> : null}
          <span style={{ fontSize: "calc(13px * var(--dz-fs, 1))", color: "var(--dz-ink)" }}>Duration required</span>
          <Switch size="small" checked={on} disabled={readOnly} onChange={toggle} data-testid="duration-required" />
        </Space>
      </Flex>
      {on ? (
      <Flex vertical gap={6} style={{ width: "100%" }}>
          <>
            <Flex gap={8} wrap="wrap">
              {tp(min, setMin, "Min (HH:MM)")}
              {tp(max, setMax, "Max (HH:MM)")}
              <div style={{ flex: "1 1 100%", minWidth: 0 }}>
                <div className="l" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))", fontWeight: 600, color: "var(--dz-field-label)", marginBottom: 2 }}>Counts from</div>
                <Select size="small" style={{ width: "100%" }} disabled={readOnly} value={from} onChange={setFrom}
                        data-testid="duration-from"
                        options={[{ value: "STEP_START", label: "When the operator starts this step" },
                                  { value: "PREVIOUS_END", label: "When the previous step is completed" }]} />
              </div>
              <div style={{ flex: "1 1 100%", minWidth: 0 }}>
                <div className="l" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))", fontWeight: 600, color: "var(--dz-field-label)", marginBottom: 2 }}>Deviation rule</div>
                <Select size="small" style={{ width: "100%" }} disabled={readOnly} value={dev} onChange={setDev}
                        options={DEVIATION_RULES} data-testid="duration-deviation" />
              </div>
            </Flex>
            <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
              {dev === "none" ? "Shown as the expected time only — nothing is timed and no deviation is raised."
                : `A deviation is raised when the step ${{ less_than_min: "finishes before the Min", greater_than_max: "runs longer than the Max",
                    less_than_min_or_greater_than_max: "finishes before the Min or runs longer than the Max" }[dev] || ""}.`}
              {dev !== "none" ? (from === "PREVIOUS_END"
                ? " The timer starts by itself the moment the previous step is completed — waiting before starting counts."
                : " The timer starts when the operator presses Start on this step.") : null}
            </Text>
            {!persisted && !readOnly ? (
              <Input size="small" placeholder="SOP reference — the clause this time comes from (optional)"
                     value={ref} onChange={(e) => setRef(e.target.value)} />
            ) : null}
            {error && !readOnly ? <Text type="danger" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>{error}</Text> : null}
            {!readOnly ? (
              <div>
                <Button size="small" type="primary" disabled={!canApply} onClick={apply} data-testid="duration-apply">
                  {persisted ? "Update duration" : "Set duration"}
                </Button>
              </div>
            ) : null}
          </>
      </Flex>
      ) : null}
    </div>
  );
}

function BindRule({ rules, bound, onBind, operations = [], operationCode = null }) {
  const [open, setOpen] = useState(false);
  const [authoring, setAuthoring] = useState(false);
  const [draft, setDraft] = useState({ rule_type: "LIMIT", name: "", subject: "", provenance: "CITED", source_ref: "", min_value: null, max_value: null, unit: "" });
  // PREVIEW ONLY — CR-10, not ruled. A SEQUENCE rule states which OPERATION this one waits
  // for, so it carries a predecessor where the other eight carry a value. It is also the one
  // rule that cannot live in the library: "after Dispensing" means this record's Dispensing,
  // and another record's is a different operation. So it is authored here, never bound from
  // a shared row — which is itself a sign it is not the same kind of thing as the other eight.
  const isSequence = draft.rule_type === "SEQUENCE";
  // A timed rule (CLOCK / CADENCE) is a window in time, not a value: authored as minutes, and one
  // side at least — a clock with no window judges nothing (readiness: CLOCK_HAS_NO_WINDOW).
  const isTimed = draft.rule_type === "CLOCK" || draft.rule_type === "CADENCE";
  const timedMissing = isTimed && (draft.min_min == null || draft.min_min === "") && (draft.max_min == null || draft.max_min === "");
  const authored = () => {
    if (!isTimed) return { ...draft };
    const { min_min, max_min, min_value, max_value, unit, ...rest } = draft;
    return { ...rest, min_duration: hhmmss(min_min), max_duration: hhmmss(max_min) };
  };
  // Never itself; a record's own operations only.
  const predecessors = (operations || []).filter((o) => o.code !== operationCode);
  const already = new Set(bound.map((b) => b.inherited_from).filter(Boolean));
  const groups = new Map();
  rules.forEach((r) => { const k = r.template?.name || "Library"; if (!groups.has(k)) groups.set(k, []); groups.get(k).push(r); });
  const menu = (
    <div className="u0-toolbox" onClick={(e) => e.stopPropagation()}>
      <div className="u0-toolhead"><span className="q">Bind a rule</span>
        <Button size="small" type="link" onClick={() => setAuthoring((v) => !v)}>{authoring ? "From the library" : "Author one here"}</Button></div>
      <div className="u0-toolbody">
        {authoring ? (
          <div className="u0-authrule">
            <Select size="small" value={draft.rule_type} onChange={(v) => setDraft({ ...draft, rule_type: v })}
                    options={["GUARD","LIMIT","CLOCK","CADENCE","BALANCE","POLICY","ESCALATION","TREND","SEQUENCE"].map((v) => ({ value: v, label: v }))} />
            {isSequence ? (
              <>
                <Select size="small" placeholder="Runs after — which operation must finish first"
                        value={draft.after || undefined} popupMatchSelectWidth={false}
                        onChange={(v) => setDraft({ ...draft, after: v, name: `Runs after ${(predecessors.find((o) => o.code === v) || {}).name || v}` })}
                        options={predecessors.map((o) => ({ value: o.code, label: `${o.code} — ${o.name}` }))} />
                <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))" }}>
                  Nothing bound here means nothing waits: the two operations may run at the same time.
                </Text>
              </>
            ) : (
              <>
                <Input size="small" placeholder="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                <Input size="small" placeholder="Subject — one of what: a material, an instrument, a room" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
                {isTimed ? (
                  <Space size={6}>
                    <InputNumber size="small" min={0} placeholder="min (minutes)" style={{ width: 130 }} value={draft.min_min} onChange={(v) => setDraft({ ...draft, min_min: v })} />
                    <InputNumber size="small" min={0} placeholder="max (minutes)" style={{ width: 130 }} value={draft.max_min} onChange={(v) => setDraft({ ...draft, max_min: v })} />
                    <span className="u0-req" aria-label="required">*</span>
                  </Space>
                ) : (
                <Space size={6}><InputNumber size="small" placeholder="min" value={draft.min_value} onChange={(v) => setDraft({ ...draft, min_value: v })} />
                  <InputNumber size="small" placeholder="max" value={draft.max_value} onChange={(v) => setDraft({ ...draft, max_value: v })} />
                  <Input size="small" placeholder="unit" style={{ width: 80 }} value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} /></Space>
                )}
                {timedMissing ? <Text type="danger" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>A {draft.rule_type.toLowerCase()} needs a minimum or a maximum time.</Text> : null}
              </>
            )}
            <Input size="small" placeholder="Citation — the clause this comes from (required)" value={draft.source_ref} onChange={(e) => setDraft({ ...draft, source_ref: e.target.value })} />
            {isSequence ? (
              <Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))", color: "var(--dz-warn)" }}>
                Preview — SEQUENCE is not a rule type the server accepts yet (CR-10). Binding one
                here will be refused until the vocabulary ruling is made.
              </Text>
            ) : null}
            <Button size="small" type="primary"
                    disabled={(isSequence ? !draft.after : !draft.name) || !draft.source_ref || timedMissing}
                    onClick={() => { const b = authored(); onBind(b, { ...b, params: isTimed ? { min_duration: draft.min_min != null && draft.min_min !== "" ? draft.min_min * 60 : null, max_duration: draft.max_min != null && draft.max_min !== "" ? draft.max_min * 60 : null } : undefined }); setOpen(false); }}>Bind</Button>
          </div>
        ) : groups.size === 0 ? (
          <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>The library holds no rules yet.</Text>
        ) : [...groups.entries()].map(([name, items]) => (
          <div key={name} className="u0-toolgrp">
            <div className="u0-eyebrow">{name}</div>
            <div className="u0-tools">
              {items.map((r) => (
                <button key={r.id} type="button" className="u0-tool" disabled={already.has(r.id)}
                        title={already.has(r.id) ? "Already bound to this step" : r.subject}
                        onClick={() => { onBind({ library: r.id }, { ...r, inherited_from: r.id }); setOpen(false); }}>
                  <code>{r.rule_type}</code><span className="n">{r.name}</span>
                  <span className="h">{[r.min_value != null ? `≥ ${r.min_value}` : null, r.max_value != null ? `≤ ${r.max_value}` : null].filter(Boolean).join(" · ")}{r.unit ? ` ${r.unit}` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="u0-toolfoot"><Text type="secondary" style={{ fontSize: "calc(11px * var(--dz-fs, 1))" }}>
        A step binds to a rule. It never owns the value — that is what stops one plant's limit becoming a fork of the product.
      </Text></div>
    </div>
  );
  return (
    <Dropdown popupRender={() => menu} trigger={["click"]} open={open} onOpenChange={setOpen} rootClassName="dz" destroyOnHidden>
      <button type="button" className="u0-addchip">+ bind a rule</button>
    </Dropdown>
  );
}


/** The checklist's items, in order, edited where the step is: add, move, remove. The list is
 *  the shared master's — a change reaches every step that binds it, and the editor says so. */
/** 2026-10-14 (owner) · "View N items" — a chosen list's items, shown only when asked for. */
function ListToggle({ open, count, noun = "item", onToggle }) {
  return (
    <button type="button" className="u0-listtoggle" aria-expanded={open} onClick={onToggle}>
      <span className="cv">{open ? "▾" : "▸"}</span>
      {open ? "Hide" : "View"} {count} {noun}{count === 1 ? "" : "s"}
    </button>
  );
}

function ChecklistEditor({ step, readOnly, ops, library = [] }) {
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  // The items of the list the step points at — read from the library, so a list just picked
  // (and not yet saved onto the step) shows what it holds, and "Add item" appends to that.
  // The step's own copy is the fallback, for a library that has not loaded.
  const lib = library.find((c) => String(c.id) === String(step.checklist_id));
  const items = lib?.items || step.checklist_items || [];
  const ids = lib?.item_ids || step.checklist_item_ids || [];
  const shared = (step.checklist_used_by || 1) > 1;
  const canEdit = !readOnly && ops && ids.length === items.length;
  const move = (i, d) => { const next = [...ids]; const [x] = next.splice(i, 1); next.splice(i + d, 0, x); ops.setItems(step.checklist_id, next); };
  const remove = (i) => ops.setItems(step.checklist_id, ids.filter((_, k) => k !== i));
  // 2026-10-14 (owner) · the selected list shows; its items open on "View". An empty list is open,
  // so its warning and the box to add the first item are in sight.
  if (items.length && !open) return <ListToggle open={false} count={items.length} onToggle={() => setOpen(true)} />;
  return (
    <div style={{ marginTop: 4 }}>
      {items.length ? <ListToggle open count={items.length} onToggle={() => setOpen(false)} /> : null}
      {shared ? <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))", display: "block" }}>Shared list — used on {step.checklist_used_by} steps; a change here reaches every one of them.</Text> : null}
      {items.length ? (
        <ol className="u0-checklist u0-checklist-edit">
          {items.map((it, i) => (
            <li key={`${i}-${it}`}>
              <span className="txt">{it}</span>
              {canEdit ? (
                <span className="ops">
                  <Button size="small" type="text" disabled={i === 0} aria-label="Move item up" onClick={() => move(i, -1)}>↑</Button>
                  <Button size="small" type="text" disabled={i === items.length - 1} aria-label="Move item down" onClick={() => move(i, 1)}>↓</Button>
                  <Button size="small" type="text" danger aria-label="Remove item" icon={<DeleteOutlined />} onClick={() => remove(i)} />
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : <Text type="warning" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>This list has no items — the operator would see an empty checklist. Add the items below.</Text>}
      {canEdit ? (
        <Flex gap={6} style={{ marginTop: 6 }}>
          <Input size="small" placeholder="New item — what the operator confirms" value={draft} onChange={(e) => setDraft(e.target.value)}
                 onPressEnter={async () => { if (draft.trim()) { await ops.addItem(step.checklist_id, draft.trim(), ids); setDraft(""); } }} />
          <Button size="small" type="primary" disabled={!draft.trim()} onClick={async () => { await ops.addItem(step.checklist_id, draft.trim(), ids); setDraft(""); }}>Add item</Button>
        </Flex>
      ) : null}
    </div>
  );
}

/** A clearance profile's captures, behind "View N captures". */
function CaptureList({ caps }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ListToggle open={open} count={caps.length} noun="capture" onToggle={() => setOpen(!open)} />
      {open ? (
        <ol className="u0-checklist">
          {caps.map((c, i) => <li key={i}><span className="txt">{c}</span></li>)}
        </ol>
      ) : null}
    </>
  );
}

/** A new list, named here and bound at once. */
function NewListRow({ onCreate }) {
  const [name, setName] = useState("");
  return (
    <Flex gap={6} style={{ marginTop: 6 }}>
      <Input size="small" placeholder="New checklist — name it, then add its items" value={name} onChange={(e) => setName(e.target.value)} />
      <Button size="small" disabled={!name.trim()} onClick={async () => { await onCreate(name.trim()); setName(""); }}>Create list</Button>
    </Flex>
  );
}

/** A new parameter on the master, from the step: name, category, data type, unit. */
/** 2026-10-21 (owner) · the step-type list, grouped as the inserter groups it, each name with its code —
 *  readable at a glance ("width proper, all options should look properly"). Falls back to the flat list. */
function stepTypeGroups(vocab, flat, keep = null) {
  const types = (vocab?.step_types || []).filter((t) => !keep || keep(t.value));
  if (!(vocab?.step_types || []).length) return keep ? (flat || []).filter((t) => keep(t.value ?? t)) : flat;
  const groups = [];
  types.forEach((t) => {
    const name = t.group || "Other";
    let g = groups.find((x) => x.title === name);
    if (!g) { g = { title: name, label: name, options: [] }; groups.push(g); }
    g.options.push({ value: t.value, label: t.label, code: t.code || "", search: `${t.label} ${t.code || ""} ${name}` });
  });
  return groups;
}

function NewParameterRow({ categories, units, vocab, onCreate }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", control_category_id: undefined, sub_category_id: undefined, data_type: undefined, unit_id: undefined });
  const cat = categories.find((c) => c.id === f.control_category_id);
  const types = (cat?.data_types?.length ? cat.data_types : ["Float", "Integer", "Boolean", "Date", "Text"]).map((t) => ({ value: t, label: t }));
  const unitOptions = (cat?.units?.length ? cat.units : units).map((u) => ({ value: u.id, label: u.name }));
  if (!open) return <Button size="small" type="link" style={{ padding: 0, height: "auto", marginTop: 4 }} onClick={() => setOpen(true)}>New parameter on the master…</Button>;
  return (
    <div className="u0-lead" style={{ marginTop: 6 }}>
      <span className="dz-eyebrow">New parameter</span>
      <Input size="small" placeholder="Name — e.g. Bulk temperature" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <Flex gap={6} wrap>
        <Select size="small" style={{ minWidth: 170, flex: 1 }} placeholder="Control category" value={f.control_category_id} showSearch optionFilterProp="label"
                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                onChange={(v) => { const c = categories.find((x) => x.id === v); setF({ ...f, control_category_id: v, sub_category_id: undefined, data_type: c?.data_types?.length === 1 ? c.data_types[0] : f.data_type, unit_id: c?.units?.length === 1 ? c.units[0].id : f.unit_id }); }} />
        {cat?.sub_categories?.length ? (
          <Select size="small" style={{ minWidth: 150, flex: 1 }} placeholder="Sub-category" allowClear value={f.sub_category_id}
                  options={cat.sub_categories.map((sc) => ({ value: sc.id, label: sc.name }))} onChange={(v) => setF({ ...f, sub_category_id: v })} />
        ) : null}
        <Select size="small" style={{ width: 120 }} placeholder="Data type" value={f.data_type} options={types} onChange={(v) => setF({ ...f, data_type: v })} />
        <Select size="small" style={{ width: 100 }} placeholder="Unit" allowClear value={f.unit_id} options={unitOptions} onChange={(v) => setF({ ...f, unit_id: v })} />
      </Flex>
      <Flex gap={6}>
        <Button size="small" type="primary" disabled={!f.name.trim() || !f.control_category_id || !f.data_type}
                onClick={async () => { await onCreate({ name: f.name.trim(), control_category_id: f.control_category_id, sub_category_id: f.sub_category_id, data_type: f.data_type, unit_id: f.unit_id }); setOpen(false); setF({ name: "", control_category_id: undefined, sub_category_id: undefined, data_type: undefined, unit_id: undefined }); }}>
          Create and record it here
        </Button>
        <Button size="small" onClick={() => setOpen(false)}>Cancel</Button>
      </Flex>
      <Text type="secondary" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>Name, category and data type are required: the type is what a reading is checked against.</Text>
    </div>
  );
}

/** The parameter master as a grouped pick list: one group per control category, each option
 *  naming the parameter and its type and unit, so what is being chosen is legible in the list. */
export function groupedParameters(list) {
  const groups = new Map();
  for (const p of list) {
    const key = p.category || "Uncategorised";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ value: p.id, label: `${p.name}${p.data_type ? ` · ${p.data_type}` : ""}${p.unit ? ` ${p.unit}` : ""}${p.sub_category ? ` · ${p.sub_category}` : ""}` });
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([label, options]) => ({ label, options }));
}

/** What is collected, and why. The master says what (type, unit, category, instrument,
 *  location); the step says why (the attribute it moves, the limit it is judged against,
 *  what it records) — and says so out loud when nothing on the step does yet. */
function ParameterCard({ pm, step, limits, cpp }) {
  const help = useHelp();
  const why = [];
  if ((cpp || []).length) why.push(`moves ${cpp.map((e) => e.attribute).join(", ")}`);
  if ((limits || []).length) why.push(`judged against ${limits.map((r) => `${r.name}${r.min_value != null ? ` ≥ ${r.min_value}` : ""}${r.max_value != null ? ` ≤ ${r.max_value}` : ""}${r.unit ? ` ${r.unit}` : ""}`).join("; ")}`);
  const watched = monitoringSentence(step.monitoring);
  if (watched) why.push(watched);
  if (step.records) why.push(`written to the record as "${step.records}"`);
  const rows = [
    ["Parameter", pm.name + (pm.unique_id ? ` · ${pm.unique_id}` : "")],
    ["Kind of control", [pm.category, pm.sub_category].filter(Boolean).join(" › ") || "—"],
    ["Data type", pm.data_type || "not stated on the master — a reading cannot be type-checked"],
    ["Unit", pm.unit || "—"],
    ["Instrument", pm.instrument || "any"],
    ["Location", pm.location || "—"],
  ];
  return (
    <div className="u0-lead" style={{ marginTop: 6 }}>
      <span className="dz-eyebrow">What is collected</span>
      {rows.map(([k, v]) => <div key={k} className="u0-kv"><span className="k">{k}</span><span className="v" style={!pm.data_type && k === "Data type" ? { color: "var(--dz-warn)" } : undefined}>{v}</span></div>)}
      {/* 2026-10-14 · generated text, not an input: shown only with "Show help" (step panel redesign). */}
      {!help ? null : <span className="dz-eyebrow" style={{ marginTop: 6 }}>Why it is collected</span>}
      {!help ? null : why.length ? (
        <ul className="u0-checks">{why.map((w, i) => <li key={i}>{w}</li>)}</ul>
      ) : (
        <Text type="warning" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>
          Nothing on this step says why yet. Tick the quality attribute it moves, bind the limit it is judged against, or say what it records.
        </Text>
      )}
    </div>
  );
}

//: What the monitoring editor may send. The server refuses anything else, so the read-only
//: facts the row also carries (its code, control, unit, ceiling) never travel back.


// 2026-10-22 (owner) · "dynamic radio button, dropdown list aur checkbox bhi add karna hai": a field of these
// types carries the choices its author lists — at least two for radio / dropdown, one for checkboxes.
const CHOICE_TYPES = { RADIO: 2, SELECT: 2, CHECKBOX: 1 };
const isChoice = (t) => Object.prototype.hasOwnProperty.call(CHOICE_TYPES, t);
const choicesShort = (t, opts) => isChoice(t) && (opts || []).length < CHOICE_TYPES[t];

/** The choices of a choice field: typed one by one (Enter adds), shown in order, each removable. */
function ChoiceOptions({ type, value, onChange, readOnly }) {
  const opts = value || [];
  const short = choicesShort(type, opts);
  return (
    <div style={{ flexBasis: "100%", marginTop: 4 }}>
      <Select size="small" mode="tags" open={false} disabled={readOnly} style={{ width: "100%" }}
              value={opts} tokenSeparators={[]} status={short ? "error" : undefined}
              placeholder={`Type a choice and press Enter${CHOICE_TYPES[type] > 1 ? " — at least 2" : ""}`}
              onChange={(v) => onChange((v || []).map((x) => String(x).trim()).filter((x) => x && !x.includes(";")))} />
      <Text type={short ? "danger" : "secondary"} style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
        {type === "CHECKBOX" ? "The operator ticks any of these." : type === "RADIO" ? "The operator picks one — shown as radio buttons."
          : "The operator picks one from a dropdown."}
        {short ? ` Add at least ${CHOICE_TYPES[type]} choice${CHOICE_TYPES[type] > 1 ? "s" : ""}.` : ""}
      </Text>
    </div>
  );
}

/** RV9m · the values the operator writes on this step, each named from the label master and
 *  typed from the closed vocabulary. Written as changed — a field is its own row. */
function CollectedFields({ step, readOnly, masters, fieldOps, vocab, lead, onPendingRow }) {
  const [draft, setDraft] = useState({ label_id: undefined, data_type: "TEXT", required: true, unit_id: undefined });
  // What is typed in the label picker — offered as a new label when the master has no such name.
  const [labelSearch, setLabelSearch] = useState("");
  const [addingLabel, setAddingLabel] = useState(false);
  const [newLabel, setNewLabel] = useState(null);     // the add box, once edited (null = follow the search)
  const [labelOpen, setLabelOpen] = useState(false);
  const fields = step.fields || [];
  const types = vocab.value_types || [];
  const canEdit = !readOnly && fieldOps;
  const used = new Set(fields.filter((f) => f.pending !== "remove").map((f) => f.label_id));
  // A field chosen in the add row but not yet added still counts as a change: the form's Save
  // turns on, and Save adds it (StepForm). Reported up as it is chosen.
  useEffect(() => { onPendingRow?.(draft.label_id ? { ...draft } : null); }, [draft]);   // eslint-disable-line react-hooks/exhaustive-deps
  const numeric = (t) => t === "FLOAT" || t === "INTEGER";
  // 2026-10-29 (owner) · a read-only step shows only what was configured: no fields, no row.
  if (readOnly && !fields.length) return null;
  return (
    <Field label={lead === "evidence" ? "What the operator writes" : "Fields the operator fills"}
           hint="An action-list field: its label from the label master, its type from the vocabulary, whether it is required, and its unit. A field with no type cannot be checked at execution.">
      {fields.length ? (
        <div className="u0-fields">
          {fields.map((f) => (
            <div key={f.id || f.label} className="u0-fieldrow"
                 style={f.pending === "remove" ? { opacity: 0.55, textDecoration: "line-through" } : undefined}>
              <span className="lb">{f.label}
                {f.pending ? <span className="dz-chip" style={{ marginLeft: 6, textDecoration: "none", color: "var(--dz-warn, #ad6800)" }}>
                  {f.pending === "remove" ? "removed — not saved" : "not saved"}</span> : null}
              </span>
              {canEdit && f.id ? (
                <Select size="small" value={f.data_type} options={types}
                        onChange={(v) => fieldOps.edit(step.code, f.id, { data_type: v, options: isChoice(v) ? (f.options || []) : [] })} />
              ) : <span className="dz-chip">{(types.find((t) => t.value === f.data_type) || {}).label || f.data_type}</span>}
              {numeric(f.data_type) ? (canEdit && f.id ? (
                <Select size="small" allowClear placeholder="unit" className="unit" value={f.unit_id ?? undefined}
                        options={(masters.units || []).map((u) => ({ value: u.id, label: u.name }))}
                        onChange={(v) => fieldOps.edit(step.code, f.id, { unit_id: v || null })} />
              ) : (f.unit ? <span className="dz-chip">{f.unit}</span> : null)) : null}
              {canEdit && f.id ? (
                <label className="req"><input type="checkbox" checked={!!f.required} onChange={(e) => fieldOps.edit(step.code, f.id, { required: e.target.checked })} /> required</label>
              ) : <span className="dz-chip">{f.required ? "required" : "optional"}</span>}
              {/* 2026-10-23 (owner) · on the field's own line, beside its name and type — under the options it read
                  as removing an option, not the field. */}
              {canEdit && f.id && f.pending === "remove" ? (
                <Button size="small" type="link" onClick={() => fieldOps.undoRemove?.(f.id)}>Undo</Button>
              ) : canEdit && f.id ? (
                <Tooltip title={`Remove field “${f.label}”`}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={`Remove field ${f.label}`}
                          onClick={() => fieldOps.remove(step.code, f.id)} />
                </Tooltip>
              ) : null}
              {isChoice(f.data_type) ? (canEdit && f.id ? (
                <ChoiceOptions type={f.data_type} value={f.options} onChange={(v) => fieldOps.edit(step.code, f.id, { options: v })} />
              ) : (
                <span style={{ flexBasis: "100%" }}>{(f.options || []).map((o) => <span key={o} className="dz-chip">{o}</span>)}</span>
              )) : null}
            </div>
          ))}
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
          {readOnly ? "None" : lead === "evidence" || lead === "checks" ? "Nothing is written here yet — add the fields the operator fills." : "No fields beyond the instruction."}
        </Text>
      )}
      {canEdit ? (
        <Flex gap={6} align="center" wrap style={{ marginTop: 6 }}>
          {(() => {
            // First row of the dropdown: a box of its own to type a NEW label and add it; below
            // it, the master's labels to pick from. The box starts with whatever was typed in
            // the search, so "type, not found, add" is one motion.
            const clean = (t) => String(t || "").trim().replace(/\s+/g, " ");
            const typed = clean(newLabel !== null ? newLabel : labelSearch);
            const existing = (masters.labels || []).find((l) => clean(l.name).toLowerCase() === typed.toLowerCase());
            const addLabel = async () => {
              if (!typed) return;
              if (existing) { setDraft((d) => ({ ...d, label_id: existing.id })); setNewLabel(null); setLabelSearch(""); setLabelOpen(false); return; }
              setAddingLabel(true);
              const id = await fieldOps.createLabel(typed);
              setAddingLabel(false);
              if (id) { setDraft((d) => ({ ...d, label_id: id })); setNewLabel(null); setLabelSearch(""); setLabelOpen(false); }
            };
            return (
              <Select size="small" showSearch optionFilterProp="label" style={{ minWidth: 220, flex: 1 }}
                      placeholder={fieldOps.createLabel ? "Label — select one, or add a new one" : "Label — from the label master"}
                      open={labelOpen} onDropdownVisibleChange={(o) => { setLabelOpen(o); if (!o) setNewLabel(null); }}
                      value={draft.label_id} searchValue={labelSearch} onSearch={setLabelSearch}
                      onChange={(v) => { setDraft({ ...draft, label_id: v }); setLabelSearch(""); setNewLabel(null); }}
                      notFoundContent="No label by that name — add it above."
                      options={(masters.labels || []).filter((l) => !used.has(l.id)).map((l) => ({ value: l.id, label: l.name }))}
                      dropdownRender={(menu) => (
                        <>
                          {fieldOps.createLabel ? (
                            <div data-testid="add-label-row"
                                 onMouseDown={(e) => e.stopPropagation()}
                                 style={{ display: "flex", gap: 6, padding: "4px 8px 8px",
                                          borderBottom: "1px solid var(--dz-field-border, #e5e7eb)", marginBottom: 4 }}>
                              <Input size="small" placeholder="New label name" value={newLabel !== null ? newLabel : labelSearch}
                                     onChange={(e) => setNewLabel(e.target.value)}
                                     onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { e.preventDefault(); addLabel(); } }} />
                              <Button size="small" type="primary" loading={addingLabel} disabled={!typed} onClick={addLabel}>
                                {existing ? "Select" : "+ Add"}
                              </Button>
                            </div>
                          ) : null}
                          {menu}
                        </>
                      )} />
            );
          })()}
          <Select size="small" style={{ width: 130 }} value={draft.data_type} options={types} onChange={(v) => setDraft({ ...draft, data_type: v })} />
          {numeric(draft.data_type) ? (
            <Select size="small" allowClear placeholder="unit" style={{ width: 90 }} value={draft.unit_id}
                    options={(masters.units || []).map((u) => ({ value: u.id, label: u.name }))} onChange={(v) => setDraft({ ...draft, unit_id: v })} />
          ) : null}
          <label className="req"><input type="checkbox" checked={draft.required} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} /> required</label>
          {isChoice(draft.data_type) ? (
            <ChoiceOptions type={draft.data_type} value={draft.options} onChange={(v) => setDraft({ ...draft, options: v })} />
          ) : null}
          {/* 2026-10-23 (owner) · after the field's options, which are part of the field being added. */}
          <Button size="small" type="primary" disabled={!draft.label_id || choicesShort(draft.data_type, draft.options)}
                  onClick={async () => { const r = await fieldOps.add(step.code, { label_id: draft.label_id, data_type: draft.data_type, required: draft.required, unit_id: numeric(draft.data_type) ? draft.unit_id : null, options: isChoice(draft.data_type) ? (draft.options || []) : [] }); if (r && r.ok !== false) setDraft({ label_id: undefined, data_type: "TEXT", required: true, unit_id: undefined, options: [] }); }}>
            Add field
          </Button>
        </Flex>
      ) : null}
    </Field>
  );
}

/**
 * One entry, three shapes. A LOOP and a DECISION are structures, not steps: the questions
 * differ, so the form differs — but a host mounts one component and never branches on kind.
 */
/** "HH:MM:SS" → seconds, for showing a staged duration the way the server will serve it. */
function secondsOf(hms) {
  if (hms == null || hms === "") return null;
  const [h, m, sec] = String(hms).split(":").map(Number);
  return (h || 0) * 3600 + (m || 0) * 60 + (sec || 0);
}

/** The step's rules with the draft's rule changes laid over them: what the author will get
 *  on Save, marked pending — a tightened limit, a policy update, an unbind, a new binding. */
function rulesWithPending(rules, ops) {
  let out = (rules || []).map((r) => ({ ...r }));
  ops.forEach((o, i) => {
    if (o.op === "tighten") {
      out = out.map((r) => r.id !== o.id ? r : {
        ...r,
        min_value: o.limits.min_value !== undefined ? o.limits.min_value : r.min_value,
        max_value: o.limits.max_value !== undefined ? o.limits.max_value : r.max_value,
        params: { ...(r.params || {}),
          ...(o.limits.min_duration !== undefined ? { min_duration: secondsOf(o.limits.min_duration) } : {}),
          ...(o.limits.max_duration !== undefined ? { max_duration: secondsOf(o.limits.max_duration) } : {}),
          ...(o.limits.params || {}) },
        pending: "tighten" });
    } else if (o.op === "refresh") {
      out = out.map((r) => r.id !== o.id || !r.policy ? r : {
        ...r, params: { ...(r.params || {}), min_duration: r.policy.policy.min_duration, max_duration: r.policy.policy.max_duration, ...(r.policy.policy.params || {}) },
        policy: { ...r.policy, differs: false }, pending: "policy" });
    } else if (o.op === "unbind") {
      out = out.map((r) => r.id !== o.id ? r : { ...r, pending: "unbind" });
    } else if (o.op === "bind") {
      out = [...out, { id: `pending-${i}`, binding: `pending-${i}`, pending: "bind", ...(o.preview || {}) }];
    }
  });
  return out;
}

/** The step's fields with the staged changes laid over them, each marked as not saved. */
function fieldsWithPending(fields, ops, masters = {}) {
  let out = [...(fields || [])];
  const nameOf = (id) => ((masters.labels || []).find((l) => l.id === id) || {}).name || "";
  const unitOf = (id) => ((masters.units || []).find((u) => u.id === id) || {}).name || "";
  ops.forEach((o) => {
    if (o.op === "add") {
      out = [...out, { id: o.tmp, label_id: o.body.label_id, label: nameOf(o.body.label_id), data_type: o.body.data_type,
                       required: o.body.required, unit_id: o.body.unit_id, unit: unitOf(o.body.unit_id),
                       options: o.body.options || [], pending: "add" }];
    } else if (o.op === "edit") {
      out = out.map((f) => (f.id !== o.id ? f : { ...f, ...o.body,
        ...(o.body.unit_id !== undefined ? { unit: unitOf(o.body.unit_id) } : {}), pending: f.pending || "edit" }));
    } else if (o.op === "remove") {
      out = out.map((f) => (f.id !== o.id ? f : { ...f, pending: "remove" }));
    }
  });
  return out;
}

/**
 * 2026-10-07 · the mandatory facts of each step type's form, checked before Save — the same
 * rules the server applies (ingredient_settings.py, monitoring.py, sampling.py), so the author
 * is told on the form and nothing half-filled is sent. Returns the first problem, or null.
 */
/**
 * 2026-10-22 (owner) · what a dispense step is not saved without: a material from the bill of materials, each
 * with a ± tolerance (its own or its role's — `tolerance_percent`, record_refs), and the balance it is weighed on
 * (its equipment). The material is the step's own, else each material of its repeat, else the lines the bill
 * dispenses in this operation.
 */
function dispenseGaps(step, { bomLines = [], operation = null, inLoop = false, derived = {} } = {}) {
  const find = (nodes, c) => {
    for (const n of nodes || []) {
      if (n.code === c) return n;
      const hit = find(n.children, c);
      if (hit) return hit;
    }
    return null;
  };
  let codes = [];
  if (step?.material_sap_code) codes = [String(step.material_sap_code).trim()];
  else if (inLoop) {
    const dep = (derived?.[`node:${step?.code}#membership`]?.dependencies || [])[0] || "";
    const loop = find(operation?.children, String(dep).replace(/^node:/, ""));
    codes = (loop?.subjects || []).map((x) => String(x.sap_code || "").trim()).filter(Boolean);
  }
  const lines = codes.length
    ? (bomLines || []).filter((l) => codes.includes(String(l.sap_code || "").trim()))
    : (bomLines || []).filter((l) => operation?.id != null && String(l.dispensing_operation) === String(operation.id));
  const out = {};
  if (!lines.length && !(inLoop && codes.length)) {
    out.material = inLoop
      ? "The repeat names no material from the bill of materials — choose what it repeats over"
      : "No material of the bill of materials is dispensed here — set the material's dispensing operation in the Bill of materials";
  }
  const untoleranced = lines.filter((l) => l.tolerance_percent === null || l.tolerance_percent === undefined);
  if (untoleranced.length) {
    out.tolerance = `Give ${untoleranced.map((l) => l.material || l.sap_code).join(", ")} a ± tolerance in the Bill of materials before saving`;
  }
  const booths = step?.booth_ids ?? (step?.booths || []).map((b) => b.id);
  if (!(booths || []).length) out.balance = "Choose the balance this step is weighed on (Equipment) before saving";
  return out;
}

function mandatoryProblem(step, { inLoop = false, operation = null } = {}) {
  const t = step?.step_type;
  const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  // A charge / addition / transfer names the material it moves — except inside a repeat, where
  // the material comes from the collection, one per pass.
  if (INGREDIENT_STEP_TYPES.includes(t)) {
    // 2026-10-13 · a DISPENSING step offers no material picker — it dispenses what the bill of
    // materials lists — so it is never refused for not naming one.
    const ing = step.ingredient || {};
    if (t === "TRANSFER") {
      // 2026-10-29 (owner) · a transfer, as /process-config's Add Ingredients: an RM is picked from the bill (RM is
      // the type until changed), a WIPM / Product is named, and the execution mode is chosen.
      const mt = ing.material_type || "RM";
      if (mt === "RM" && !inLoop && !step.material_sap_code) return "Choose the RM this step transfers before saving";
      if ((mt === "WIPM" || mt === "Product") && !String(ing.wip_material_name || "").trim()) return "Enter the RM name before saving";
      if (!ing.execution_mode) return "Choose the execution mode (Dispense type) before saving";
    } else if (!inLoop && !step.material_sap_code && t !== "DISPENSING") return "Choose the material this step moves before saving";
    if (ing.cond_parameter_id && num(ing.cond_min) === null) return "Give the conditional parameter its minimum before saving";
    if (num(ing.cond_min) !== null && num(ing.cond_max) !== null && num(ing.cond_max) < num(ing.cond_min)) {
      return "The conditional maximum is below the minimum";
    }
    if (ing.number_of_lots !== undefined && ing.number_of_lots !== null && !(Number(ing.number_of_lots) >= 1)) {
      return "The number of lots is 1 or more";
    }
  }
  // A sample says what, how much, where it goes and what it is tested for.
  if (t === "SAMPLE" && !isAqlSampleStep(step, operation)) {
    const sm = step.sampling || {};
    const missing = [!sm.sample_id && "the sample", (sm.quantity === null || sm.quantity === undefined || sm.quantity === "") && "the quantity",
      !sm.to_id && "the department", !sm.test_type_id && "the test type"].filter(Boolean);
    if (missing.length) return `Fill the sampling: ${missing.join(", ")}`;
    if (sm.is_qc && num(sm.sampling_min) === null) return "A QC sample needs its sampling minimum";
    if (num(sm.sampling_min) !== null && num(sm.sampling_max) !== null && num(sm.sampling_max) < num(sm.sampling_min)) {
      return "The sampling maximum is below the minimum";
    }
    if (sm.parameter_id && num(sm.min) === null) return "Give the conditional parameter its minimum";
    if (sm.parameter_id && !String(sm.description || "").trim()) return "Say why the conditional parameter applies (description)";
  }
  // A recorded parameter states how it is read: the range and the schedule.
  if (t === "PARAMETER_RECORD" && step.parameter_id) {
    const m = step.monitoring || MON_DEFAULTS;
    // 2026-10-30 · a captured value read at a moment is read once — its schedule is the server's to set.
    const readOnce = m.source === "CAPTURED" && m.read_at !== "INTERVAL";
    if (num(m.min) !== null && num(m.max) !== null && num(m.min) > num(m.max)) return "The range is inside out: the minimum is above the maximum";
    if (!readOnce && (m.schedule || "Periodic") === "Periodic" && !(num(m.every) > 0)) return "Say how often a reading is taken (Every) before saving";
    if (!readOnce && m.schedule === "Count" && !(num(m.readings) > 0)) return "Give the number of readings before saving";
    if (m.on_out_of_range === "CORRECT" && !(num(m.attempts) > 0)) return "Correct and re-check needs at least one correction attempt";
    if (m.track_duration && num(m.duration_min) !== null && num(m.duration_max) !== null && num(m.duration_max) < num(m.duration_min)) {
      return "The maximum duration is below the minimum";
    }
    if ((m.target_type || "Machine") === "Location" && !String(m.location_area || "").trim()) return "Choose the location the reading is taken at";
  }
  return null;
}

export default function StepForm(props) {
  // Edits collect in a draft and reach the record on Save — one write, one version bump,
  // one thing to undo — rather than a write per field the moment it loses focus. The form
  // below sees the step with the draft laid over it, so what the author typed is what they
  // see until they save or discard. Rule changes (tighten, update from policy, unbind, bind)
  // are part of the same draft: they show as pending on the row and are applied, in order,
  // when the author saves — never the moment they are clicked.
  const [draft, setDraft] = useState({});
  const [gen, setGen] = useState(0);
  const code = props.step?.code;
  // 2026-10-22 (owner) · "Save button pe lagega validation, jo empty hai wo red dikhega": a required field
  // left empty turns red once Save is pressed, not before.
  const [tried, setTried] = useState(false);
  useEffect(() => { setDraft({}); setTried(false); }, [code]);
  const ruleOps = draft._rule_ops || [];
  // 2026-10-12 · the fields the operator fills are part of the same draft: added, changed or
  // removed here, they turn Save on and reach the record on Save — not the moment they are
  // clicked, which left Save off while the panel had changed. Plus a field chosen in the add
  // row but not added yet (pendingField): Save adds it.
  const fieldOpsList = draft._field_ops || [];
  const [pendingField, setPendingField] = useState(null);
  useEffect(() => { setPendingField(null); }, [code]);
  const fieldKeys = Object.keys(draft).filter((k) => k !== "_rule_ops" && k !== "_field_ops");
  // What is being typed, reported up so the step's row in the sequence reads it as it is
  // typed — the record still changes only on Save. Cleared when the draft empties, when
  // another step is opened, and when the form goes away.
  const onDraft = props.onDraft;
  useEffect(() => {
    if (!onDraft || !code) return;
    onDraft(code, fieldKeys.length ? Object.fromEntries(fieldKeys.map((k) => [k, draft[k]])) : null);
  }, [draft, code]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (onDraft && code) onDraft(code, null); }, [code]);   // eslint-disable-line react-hooks/exhaustive-deps
  const changes = fieldKeys.length + ruleOps.length + fieldOpsList.length + (pendingField ? 1 : 0);
  const dirty = changes > 0;
  const stage = (patch) => { if (!props.readOnly) setDraft((d) => ({ ...d, ...patch })); };
  const stageRule = (op) => { if (!props.readOnly) setDraft((d) => ({ ...d, _rule_ops: [...(d._rule_ops || []), op] })); };
  const setFieldOps = (fn) => { if (!props.readOnly) setDraft((d) => ({ ...d, _field_ops: fn(d._field_ops || []) })); };
  const stagedFieldOps = props.fieldOps ? {
    createLabel: props.fieldOps.createLabel,   // a master entry, written at once; picking it is staged
    add: (_code, body) => { setFieldOps((ops) => [...ops, { op: "add", tmp: `pending-${Date.now()}-${ops.length}`, body }]); return { ok: true }; },
    edit: (_code, id, body) => setFieldOps((ops) => (String(id).startsWith("pending-")
      ? ops.map((o) => (o.tmp === id ? { ...o, body: { ...o.body, ...body } } : o))
      : [...ops.filter((o) => !(o.op === "edit" && o.id === id)),
         { op: "edit", id, body: { ...((ops.find((o) => o.op === "edit" && o.id === id) || {}).body || {}), ...body } }])),
    remove: (_code, id) => setFieldOps((ops) => (String(id).startsWith("pending-")
      ? ops.filter((o) => o.tmp !== id)
      : [...ops.filter((o) => o.id !== id), { op: "remove", id }])),
    undoRemove: (id) => setFieldOps((ops) => ops.filter((o) => !(o.op === "remove" && o.id === id))),
  } : null;
  // The draft clears only when every write went through: a refused write (a stale version,
  // a validation) leaves what was typed in place, to be saved again or discarded.
  const save = async () => {
    if (!dirty) return;
    // 2026-10-07 · Save is not disabled for a missing required fact: pressed, it says what is
    // missing (here and in the bar, in red, with the field itself marked) and saves nothing.
    if (blockedReason) { setTried(true); message.warning(`${blockedReason}.`); return; }
    for (const o of ruleOps) {
      let res = null;
      if (o.op === "tighten") res = await props.onTighten?.(o.id, o.limits);
      else if (o.op === "refresh") res = await props.onRefresh?.(o.id);
      else if (o.op === "unbind") res = await props.onUnbind?.(o.id);
      else if (o.op === "bind") res = await props.onBind?.(o.body);
      if (res && (res.ok === false || res.conflict)) return;  // refused: the draft stays, the author sees why
    }
    if (fieldKeys.length) {
      const patch = {}; fieldKeys.forEach((k) => { patch[k] = draft[k]; });
      const res = await props.onChange?.(patch);
      if (!res || res.ok === false || res.conflict) return;
    }
    // The fields the operator fills, in the order they were changed; a field chosen in the add
    // row but not added is added last. A refused write keeps what is left, to save again.
    const fops = [...fieldOpsList, ...(pendingField ? [{ op: "add", body: {
      label_id: pendingField.label_id, data_type: pendingField.data_type, required: pendingField.required,
      unit_id: ["FLOAT", "INTEGER"].includes(pendingField.data_type) ? pendingField.unit_id : null,
      options: isChoice(pendingField.data_type) ? (pendingField.options || []) : [] } }] : [])];
    for (let i = 0; i < fops.length; i += 1) {
      const o = fops[i];
      let res = null;
      if (o.op === "add") res = await props.fieldOps?.add(code, o.body);
      else if (o.op === "edit") res = await props.fieldOps?.edit(code, o.id, o.body);
      else if (o.op === "remove") res = await props.fieldOps?.remove(code, o.id);
      if (!res || res.ok === false) {
        // What is left stays staged; the unadded row (always last) stays in its row.
        setDraft({ _field_ops: fieldOpsList.slice(i) });
        return;
      }
    }
    setDraft({});
    setTried(false);
    if (pendingField) { setPendingField(null); setGen((g) => g + 1); }
    // 2026-10-14 (owner) · every write above answered success — say so.
    const savedName = String(("title" in draft ? draft.title : props.step?.title) || props.step?.code || "Step").trim();
    message.success(`“${savedName}” saved successfully.`);
  };
  const discard = () => { setDraft({}); setPendingField(null); setGen((g) => g + 1); };
  const step = props.step ? { ...props.step, ...Object.fromEntries(fieldKeys.map((k) => [k, draft[k]])),
                              fields: fieldsWithPending(props.step.fields, fieldOpsList, props.masters),
                              rules: rulesWithPending(props.step.rules, ruleOps),
                              // the share as saved, so "available" can add it back (charge_split)
                              __saved_charge: props.step.charge_quantity,
                              // whether the pass setting is being changed here, so a pass the step is by itself reads apart from one set (InspectionPassField)
                              __pass_staged: "inspection_pass" in draft } : props.step;
  const inner = { ...props, step, onChange: stage, showErrors: tried, fieldOps: stagedFieldOps, onPendingField: setPendingField,
    onTighten: props.onTighten ? (id, limits) => stageRule({ op: "tighten", id, limits }) : undefined,
    onRefresh: props.onRefresh ? (id) => stageRule({ op: "refresh", id }) : undefined,
    onUnbind: props.onUnbind ? (id) => stageRule({ op: "unbind", id }) : undefined,
    onBind: props.onBind ? (body, preview) => stageRule({ op: "bind", body, preview }) : undefined };
  const kind = props.step?.kind;
  // Where the step is performed is required: a step is not saved until "May run in" names a
  // room. Only where there are rooms to choose from (a record; not the type library) and only
  // for a STEP — a loop or a decision is not performed anywhere of its own.
  const areaRequired = kind === "STEP" && (props.areaList || []).length > 0;
  const hasArea = ((step?.area_ids ?? (step?.areas || []).map((a) => a.id)) || []).length > 0;
  const missingArea = areaRequired && !hasArea;
  // A step is saved with a name: "Untitled step" is not something an operator can follow.
  const missingTitle = kind === "STEP" && !String(step?.title || "").trim();
  // 2026-10-07 · what each step type cannot be saved without, the same facts readiness blocks on:
  // a clearance names its profile (CLEARANCE_NO_PROFILE), a parameter record names its parameter.
  const stepType = step?.step_type;
  const missingProfile = kind === "STEP" && stepType === "CLEARANCE" && !step?.clearance_profile_id;
  const missingParameter = kind === "STEP" && stepType === "PARAMETER_RECORD" && !step?.parameter_id;
  // 2026-10-09 · the rest of what readiness blocks on (C-3, C-8), required here too.
  // Who may give it: the role picked in this draft (`signed_by_id`) counts as much as the one
  // saved (`signature_role_id`) — checking only the saved one refused Save beside a filled-in pick.
  const missingSigner = kind === "STEP" && SECOND.includes(meaningOf(step, props.signLabels || []))
    && !(step?.signed_by_id ?? step?.signature_role_id) && !step?.signature_policy;
  const loopKind = step?.bound_kind ?? step?.payload?.kind;
  const loopOver = step?.over_subject ?? step?.payload?.over_subject ?? step?.payload?.over;
  const missingOver = kind === "LOOP" && (loopKind || "OVER_COLLECTION") === "OVER_COLLECTION"
    && (!loopOver || String(loopOver).trim() === "" || loopOver === "not stated yet");
  // 2026-10-22 (owner) · "blocking ki jagah form ko validations add karo": what the record's findings used to
  // block a submit on is required on the form itself, so a step is not saved without it.
  //   every step       its instruction (what the operator does)
  //   a decision       the sampling / parameter step it is decided on
  //   a dispense       a material from the bill, each with a ± tolerance, and the balance (equipment) it is weighed on
  const missingInstruction = kind === "STEP" && !String(step?.instruction || "").trim();
  // 2026-10-29 (owner) · "Performed by" is required on every step type — who does the work is the step's
  // first signature. Not where the recipe decides the role (showRole off: there is nothing to pick).
  const missingPerformer = kind === "STEP" && props.showRole !== false
    && (step?.performed_by_id ?? step?.role_id) == null;
  const missingBasis = kind === "DECISION" && !step?.depends_on;
  const dispense = kind === "STEP" && stepType === "DISPENSING" ? dispenseGaps(step, props) : null;
  // 2026-10-22 (owner) · "cleaning step type ke liye equipment selection mandatory, jaise area mandatory hai":
  // a cleaning step names the equipment it cleans, in the right panel, beside its area.
  const missingCleanedEquipment = kind === "STEP" && stepType === "CLEANING"
    && !((step?.booth_ids ?? (step?.booths || []).map((b) => b.id)) || []).length;
  // 2026-10-22 · a radio / dropdown / checkbox field is saved with its choices.
  const shortChoice = [...(step?.fields || []).filter((f) => f.pending !== "remove"),
                       ...(pendingField ? [{ ...pendingField, label: "the new field" }] : [])]
    .find((f) => choicesShort(f.data_type, f.options));
  const blockedReason = shortChoice ? `Add at least ${CHOICE_TYPES[shortChoice.data_type]} choice${CHOICE_TYPES[shortChoice.data_type] > 1 ? "s" : ""} to ${shortChoice.label || "the field"} before saving`
    : missingTitle ? "Give the step a name before saving"
    : missingInstruction ? "Write the instruction — what the operator does — before saving"
    : missingPerformer ? "Choose who performs this step (Performed by) before saving"
    : kind === "STEP" && (step?.signers || []).some((sg) => sg.role_id == null)
      ? `Choose who may give “${(step.signers.find((sg) => sg.role_id == null) || {}).label || "each signature"}” before saving`
    : missingBasis ? "Choose the sampling or parameter step this decision is made on before saving"
    : dispense?.material ? dispense.material
    : dispense?.tolerance ? dispense.tolerance
    : dispense?.balance ? dispense.balance
    : missingArea ? "Choose \"May run in\" before saving"
    : missingCleanedEquipment ? "Choose the equipment this cleaning step cleans (Equipment) before saving"
    : missingProfile ? "Choose the clearance profile before saving"
    : missingParameter ? "Choose the parameter this step records before saving"
    : missingSigner ? "Choose who may give the second signature before saving"
    : missingOver ? "Choose what this repeat runs over before saving"
    : kind === "STEP" ? mandatoryProblem(step, { inLoop: props.inLoop, operation: props.operation }) : null;
  // 2026-10-14 (owner) · Save / Discard, with the remove button and the blocking / clear pill,
  // stand in the panel card's own header for a step; a repeat or decision keeps the bar above.
  const saveBar = props.readOnly ? null : (
    <div className={`u0-savebar ${dirty ? "on" : ""}`} role="status" aria-live="polite">
      <span className={`u0-savebar-note ${blockedReason && dirty ? "blocked" : ""}`}
            style={blockedReason ? { color: "var(--dz-block, #cf1322)" } : undefined}>
        {blockedReason || (dirty ? `${changes} change${changes === 1 ? "" : "s"} not saved` : "All changes saved")}
      </span>
      <Space size={6}>
        {props.headActions || null}
        <Button size="small" onClick={discard} disabled={!dirty}>Discard</Button>
        <Tooltip title={blockedReason || ""}>
          <Button size="small" type="primary" onClick={save} disabled={!dirty}>Save</Button>
        </Tooltip>
      </Space>
    </div>
  );
  const inHeader = kind !== "LOOP" && kind !== "DECISION" && kind !== "LANE";
  let body;
  if (kind === "LOOP") {
    body = <LoopForm key={`${code}-${gen}`} step={step} operation={props.operation} derived={props.derived}
                     bomLines={props.bomLines} masters={props.masters}
                     readOnly={props.readOnly} onChange={stage}
                     onConcurrency={props.onConcurrency} />;
  } else if (kind === "DECISION" || kind === "LANE") {
    body = <DecisionForm key={`${code}-${gen}`} step={step} operation={props.operation} nodesByCode={props.nodesByCode} readOnly={props.readOnly}
                         showErrors={tried} onChange={stage} onEditNode={props.onEditNode} />;
  } else {
    body = <StepFormBody key={`${code}-${gen}`} {...inner} saveBar={saveBar} />;
  }
  return (
    <div className="u0-stepform-wrap">
      {inHeader ? null : saveBar}
      {body}
    </div>
  );
}

function StepFormBody({
  saveBar = null,
  showErrors = false,   // Save was pressed with a required field empty: mark it red
  step,
  stepTypes = [],
  signMeanings = [],
  signLabels = [],   // RV9y: [{value, label, meaning, step_types}] — the words a signature may carry
  roles = [],
  showRole = true,
  readOnly = false,
  blastRadius,
  onChange,
  mode = "design",   // "design" opens what happens and what the operator sees; "validate" the rest
  vocab = {},        // the served vocabulary: families, stages and limits
  checklists = [],   // the checklist library, [{id, name}]
  rules = [],        // the rules library, from designer/rules/
  onBind,            // (body) → bind a rule: {library: id} or an authored rule
  onTighten,         // (bindingId, limits)
  onUnbind,          // (bindingId)
  onRefresh,         // (bindingId) → update a timed rule from the plant policy it draws from
  aqlSetup = null,   // {read(code), save(code, setup)} — the AQL of a Sample step of a visual-inspection operation (services/recipe/aql_setup.py)
  files = null,      // {onUpload, onRemove, onLinkSop, onUnlinkSop, searchSops} — images, documents, procedures
  cqas = [],         // the product's quality attributes [{code, name, specification}] — CPP ticks pick from them
  masters = { parameters: [], labels: [], units: [], categories: [] },  // RV9m: the parameter, label, unit and category masters
  fieldOps = null,   // {add(code, body), edit(code, id, body), remove(code, id)} — the typed fields a step collects
  onPendingField,    // (row|null) — a field chosen in the add row and not yet added (StepForm counts it)
  checklistOps = null,     // {addItem(fieldId, name), setItems(fieldId, ids), createList(name)} — the shared master list, edited here
  onCreateParameter = null, // (body) → id — a new parameter on the master, picked at once
  operations = [],   // CR-10 preview: the record's operations, so a SEQUENCE rule can name one
  operationCode = null, // the operation this step sits in — never its own predecessor
  area = null,       // CR-09: {value, source, inherited} for this step — derived by the server
  bomLines = [],     // the record's bill of materials [{sap_code, material, role}] — what a
                     // MATERIAL_CHARGE step may point at. Never a free list: a step charges a
                     // line this record carries, or it charges nothing.
  inLoop = false,    // this step repeats; its material comes from the collection, not from here
  laneRoles = [],    // the operation's dispensing lane — which KINDS of material it handles
  areaList = [],     // the plant's areas [{id, code, name}] — the rooms a step may be worked in
  carriedEquipment = null, // {booths, from, fromTitle} — equipment carried from the equipment step above
  clearanceProfiles = [],  // the clearance library [{id, name, area, qa_format, captures}]
  derived = {},      // the server's derived facts — `#membership` names the loop this step repeats in
  nodesByCode = {},  // the record's nodes by code — the loop's own `subjects` (one per pass)
  operation = null,  // the operation this step sits in — its name heads the step (redesign)
  allowedStepTypes,  // 2026-10-29 (owner) · the step types the operation type allows; empty = all
  recordBadge = null, // "Approved v2" / "Draft" — the record's state, beside the step's (redesign)
}) {
  // The grade's label comes from the served vocabulary; the client keeps no list of its own.
  const gradeLabel = (v) => (vocab.grades || []).find((g) => g.value === v)?.label || v;
  // Stated here or inherited — either way the step is placed. Only an unplaced one is a gap.
  const hasArea = !!area?.value;
  /**
   * The material picker's list: grouped by role, and narrowed to the operation's lane when it
   * states one.
   *
   * Narrowed by the LANE, never by the step's type. `material_sap_code` does not mean "this
   * step charges it" — on this estate eight of the ten steps that name a material are
   * PARAMETER_RECORDs measuring its humidity, and one is a HOLD keeping the API at room
   * temperature. A filter off the step type would hide the material from every one of them.
   * The lane is authored on the operation, so it is a person's statement, not an inference.
   *
   * A lane that states nothing narrows nothing: an empty picker on a record whose lanes are
   * unset would read as "no materials", which is false. And the material already chosen is
   * always offered, even if it falls outside the lane — the picker reports what the record
   * says before it starts steering, or an author cannot see what they are about to lose.
   */
  /**
   * Where the work may happen, as the plant's own tree: building › floor › area › room.
   *
   * The plant itself is not a level — there is one, it is on the app header, and putting it
   * in front of every choice would be a click that decides nothing. The room is the last
   * level and today repeats the area's name, because the master maps one room per area; it
   * is here because a real plant puts several rooms in an area and this is where they will
   * appear when it does.
   *
   * What a step BINDS to is still the area (`area_ids` → `StepAreaSet`). The cascade is the
   * way in, not a second thing to resolve — a path that stops at the area is a complete
   * answer, and the room below it is the author's own precision.
   */
  const areaTree = useMemo(() => {
    const root = [];
    // 2026-09-28 · the branch levels are NOT disabled.
    //
    // They were, to stop `changeOnSelect` treating a click on "Sterile Block" as a choice —
    // but Ant disables the whole row, so the branch could not be opened either and the
    // cascade dead-ended at the first column. A building one cannot walk into is worse than
    // a building one can accidentally name, and the second is handled where it belongs: the
    // onChange below reads a path for the AREA in it, so a path that stops at a building
    // yields nothing and states nothing.
    // A leaf gets no `children` key at all: rc-cascader reads an empty array as a branch
    // with nothing in it — it draws the expand arrow and refuses to select the row.
    const at = (list, key, label, leaf = false) => {
      let node = list.find((n) => n.value === key);
      if (!node) {
        node = leaf ? { value: key, label } : { value: key, label, children: [] };
        list.push(node);
      }
      return node;
    };
    (areaList || []).forEach((a) => {
      // The site itself is registered as an "area" (AHM-SITE), and its path is empty because
      // it IS the root — so it landed at the top of the cascade beside Sterile Block and
      // Warehouse, offering the whole plant as somewhere a step is performed. The plant is
      // named on the app header; it is not a room, and it is not a choice here.
      if (!(a.path || []).length) return;
      // `path` is site › building › floor; the site is dropped as above.
      const chain = (a.path || []).slice(1);
      let level = root;
      chain.forEach((p) => { level = at(level, `n${p.id}`, p.name).children; });
      // 2026-09-28 · the area IS the leaf — the walk stops here.
      //
      // It used to open onto "Anywhere in this area" and the rooms under it, and both were
      // noise: the master maps ONE room per area and gives it the area's own name, so the
      // last column repeated the column before it and then offered a synthetic first row to
      // undo itself. The area is what the record binds to (`StepAreaSet`), so the area is
      // where the walk ends. A room level earns its place the day a real area holds several,
      // and this is the line that adds it back.
      //
      // 2026-10-26 · below the area, its rooms and the booths in them: Area › Room › Booths. A
      // booth with no room hangs straight off the area. Rooms and booths carry STRING values
      // (`r…`, `b…`) so the one number in a path is still the area the step binds to. A room with
      // no booth is not a level — it would only repeat the area.
      const label = `${a.code} · ${a.name}`;
      const boothNode = (m) => ({ value: `b${m.id}`, label: m.code ? `${m.code} · ${m.name}` : m.name });
      const roomNodes = (a.room_booths || []).filter((r) => (r.booths || []).length)
        .map((r) => ({ value: `r${a.id}-${r.id}`, label: r.name, children: r.booths.map(boothNode) }));
      const looseNodes = (a.booths || []).filter((m) => m.room_id == null && m.is_booth).map(boothNode);
      const below = roomNodes.length ? [...roomNodes, ...looseNodes] : [];
      if (below.length) level.push({ value: a.id, label, children: below });
      else at(level, a.id, label, true);
    });
    return root;
  }, [areaList]);

  /** The cascade's value: one path per chosen area, ending at the area (or its room). */
  /**
   * What the cascade SHOWS while a save is in flight.
   *
   * The control is controlled from `step.areas`, and that only changes when the server
   * answers — a designer edit is a write, not local state. So a tick appeared to do nothing:
   * the click fired, the patch went, and until it came back the control re-rendered from the
   * old value and the checkbox sprang back empty. The same went for the ×, which is why
   * removing an area looked like it had been ignored.
   *
   * This holds the author's last click until the record catches up with it. `null` means
   * "nothing pending, show what the record says" — set on every change, cleared the moment
   * `step.areas` reports the same set back, so the record stays the single source of truth
   * and this is only ever the gap between the two.
   */
  const [pendingAreas, setPendingAreas] = useState(null);
  const [pendingBooths, setPendingBooths] = useState(null);   // the booth picked in the cascade, until the record reports it
  useEffect(() => {
    if (pendingBooths === null) return;
    const saved = new Set((step.booths || []).map((b) => b.id));
    if (pendingBooths.length === saved.size && pendingBooths.every((id) => saved.has(id))) setPendingBooths(null);
  }, [step.booths, pendingBooths]);
  useEffect(() => {
    if (pendingAreas === null) return;
    const saved = new Set((step.areas || []).map((a) => a.id));
    const wanted = new Set(pendingAreas);
    if (saved.size === wanted.size && [...wanted].every((id) => saved.has(id))) {
      setPendingAreas(null);
    }
  }, [step.areas, pendingAreas]);

  // 2026-10-26 · the cascade is multi-select: several rooms, several booths. Each chosen booth shows
  // as its own path; an area with no booth chosen in it shows as the area's path.
  const shownAreaPaths = useMemo(() => {
    const ids = pendingAreas === null ? (step.areas || []).map((a) => a.id) : pendingAreas;
    const boothIds = pendingBooths === null ? (step.booths || []).map((b) => b.id) : pendingBooths;
    const pathTo = (nodes, id, trail = []) => {
      for (const n of nodes) {
        const here = [...trail, n.value];
        if (n.value === id) return here;
        const found = n.children ? pathTo(n.children, id, here) : null;
        if (found) return found;
      }
      return null;
    };
    const out = [];
    ids.forEach((id) => {
      const areaPath = pathTo(areaTree, id);
      if (!areaPath) return;
      const areaNode = areaPath.reduce((nodes, v) => (nodes || []).find((n) => n.value === v)?.children, areaTree);
      const booths = boothIds.map((bid) => pathTo(areaNode || [], `b${bid}`)).filter(Boolean)
        .map((sub) => [...areaPath, ...sub]);
      if (booths.length) out.push(...booths); else out.push(areaPath);
    });
    return out.length ? out : undefined;   // undefined, not [] — Ant reads [] as a real value
  }, [pendingAreas, pendingBooths, step.areas, step.booths, areaTree]);

  /** The cascade's choice → the step's areas and booths. A booth names itself and its area; a room
   *  names every booth in it; an area alone names the area. Booths set elsewhere (the Equipment
   *  list) that the cascade has no node for are kept. */
  const placeFromPaths = (paths) => {
    const areas = new Set();
    const booths = new Set();
    // A building or floor ticked as a whole stands for the areas under it.
    const withArea = (path) => {
      if (path.some((v) => typeof v === "number")) return [path];
      const below = path.reduce((nodes, v) => (nodes || []).find((n) => n.value === v)?.children, areaTree);
      return (below || []).flatMap((n) => withArea([...path, n.value]));
    };
    (paths || []).flatMap(withArea).forEach((path) => {
      let area = null;
      path.forEach((v) => { if (typeof v === "number") area = v; });
      if (area == null) return;
      areas.add(area);
      const last = path[path.length - 1];
      if (typeof last !== "string") {
        // The area ticked as a whole — Ant reports the area itself once every booth under it is
        // ticked — stands for all the booths under it. An area with none is just the area.
        const a = (areaList || []).find((x) => x.id === area);
        (a?.room_booths || []).forEach((r) => (r.booths || []).forEach((m) => booths.add(m.id)));
        (a?.booths || []).forEach((m) => { if (m.room_id == null && m.is_booth) booths.add(m.id); });
        return;
      }
      if (last.startsWith("b")) booths.add(Number(last.slice(1)));
      else if (last.startsWith("r")) {
        const room = (areaList || []).find((a) => a.id === area)?.room_booths
          ?.find((r) => `r${area}-${r.id}` === last);
        (room?.booths || []).forEach((m) => booths.add(m.id));
      }
    });
    const inCascade = new Set();
    (areaList || []).forEach((a) => (a.room_booths || []).forEach((r) => (r.booths || []).forEach((m) => inCascade.add(m.id))));
    (areaList || []).forEach((a) => (a.booths || []).forEach((m) => { if (m.room_id == null && m.is_booth) inCascade.add(m.id); }));
    // A machine chosen in the Equipment list stays while its area does: taking the area off the step
    // takes its machines with it. One no listed area holds is kept, as it always was.
    const home = new Map();
    (areaList || []).forEach((a) => (a.booths || []).forEach((m) => home.set(m.id, a.id)));
    (step.booth_ids ?? (step.booths || []).map((x) => x.id)).forEach((id) => {
      if (!inCascade.has(id) && (!home.has(id) || areas.has(home.get(id)))) booths.add(id);
    });
    return { area_ids: [...areas], booth_ids: [...booths] };
  };

  // 2026-10-15 (owner) · a step inside a loop offers only the materials that loop repeats over —
  // the ones ticked on the loop — not every material of the operation.
  const loopCodes = useMemo(() => {
    if (!inLoop) return null;
    const dep = (derived?.[`node:${step?.code}#membership`]?.dependencies || [])[0] || "";
    const loopNode = nodesByCode?.[String(dep).replace(/^node:/, "")];
    const codes = (loopNode?.subjects || []).map((s) => String(s.sap_code || "").trim()).filter(Boolean);
    return codes.length ? new Set(codes) : null;
  }, [inLoop, derived, nodesByCode, step?.code]);

  const materialOptions = useMemo(() => {
    const all = bomLines || [];
    const lines = loopCodes
      ? all.filter((l) => loopCodes.has(String(l.sap_code || "").trim()) || l.sap_code === step?.material_sap_code)
      : all;
    const wanted = new Set(laneRoles || []);
    const keep = wanted.size
      ? lines.filter((l) => wanted.has(l.role) || l.sap_code === step?.material_sap_code)
      : lines;
    const byRole = new Map();
    keep.forEach((l) => {
      const k = l.role || "OTHER";
      if (!byRole.has(k)) byRole.set(k, []);
      byRole.get(k).push(l);
    });
    return [...byRole.entries()].map(([role, items]) => ({
      label: (vocab.material_roles || []).find((r) => r.value === role)?.label || role,
      options: items.map((l) => {
        // 2026-09-28 · the quantity beside the name.
        //
        // The picker offered nine materials by name alone, and on this record several read
        // alike — "Excipient 1202007", "Excipient 1200436", "Propylene Glycol (USP) —
        // 1202514". The figure the record states for each is what tells them apart at the
        // moment of choosing, and the BOM already carries it. `dispense` where it differs
        // from `quantity`: that is the amount actually weighed out, which is what a charge
        // step is about.
        //
        // `label` stays the plain name so `optionFilterProp="label"` still searches names,
        // not numbers. The quantity rides in the rendered row only.
        // What is left of the line for this record as charge steps take from it (the server's
        // count, record_refs.py), beside the line's whole quantity.
        const amount = l.qs ? "q.s."
          : l.total_value != null && l.remaining_value != null && l.charged_value != null
            ? `${fmtQty(l.remaining_value)} ${l.unit || ""} left of ${fmtQty(l.total_value)} ${l.unit || ""}`.replace(/\s+/g, " ").trim()
            : (l.dispense || l.quantity || "");
        // A step names its material by SAP code, so a line without one cannot be named — it
        // is shown, disabled, with why, rather than offered as a pick that silently does
        // nothing (its value would be "", which is "no material").
        const noCode = !String(l.sap_code || "").trim();
        return {
          value: noCode ? `__nocode-${l.sequence}` : l.sap_code,
          label: l.material,
          disabled: noCode,
          title: noCode ? `${l.material} — no SAP code on the material master, so it cannot be named here`
            : amount ? `${l.material} — ${amount}` : l.material,
          render: noCode ? "no SAP code" : amount,
        };
      }),
    }));
  }, [bomLines, laneRoles, step?.material_sap_code, vocab.material_roles, loopCodes]);
  /**
   * The one material the lane leaves, when it leaves exactly one. An operation that dispenses a
   * single material — record 50's API lane is one line — offered a dropdown with one entry and
   * made the author open it to pick the only answer.
   *
   * Offered as a PROPOSAL, not written: it fills the control so the author sees it and saves it
   * with everything else. Writing it from a render would put a value in the record that nobody
   * chose, and `material_provenance` would then claim a person confirmed it (RV0 §E4 — an
   * inferred value is a proposal, and a proposal is not a decision).
   */
  const soleMaterial = useMemo(() => {
    const flat = materialOptions.flatMap((g) => g.options || []).filter((o) => !o.disabled);
    return flat.length === 1 ? flat[0].value : null;
  }, [materialOptions]);
  // 2026-10-12 · the proposal goes into the DRAFT (not the record): the picker showed the lane's only
  // material while the draft held none, so Save was refused with "Choose the material" beside a
  // filled-in picker. Staged, it is one unsaved change the author sees and confirms with Save —
  // still nothing reaches the record until a person saves it (RV0 §E4).
  useEffect(() => {
    if (!readOnly && !inLoop && soleMaterial && !step?.material_sap_code
        && INGREDIENT_STEP_TYPES.includes(step?.step_type) && step?.step_type !== "DISPENSING") {
      onChange({ material_sap_code: soleMaterial });
    }
  }, [soleMaterial, step?.material_sap_code, step?.step_type, readOnly, inLoop]); // eslint-disable-line react-hooks/exhaustive-deps
  // Defaults follow the mode, as the mock has it; a section the author opened stays open.
  // Hooks first — a return before them would change their order between renders.
  const lead = (vocab.shapes || {})[step?.step_type]?.lead;
  const defaults = useMemo(() => ({
    basic: true, does: true, instruction: true, docs: lead === "evidence", ingredient: lead === "material", monitoring: lead === "records", sampling: true,
    // CR-09 · open while the step has no area. A closed section is where the gap that stops a
    // record from being worked went unnoticed; once an area is in force the section folds back
    // and the badge carries the fact.
    runs: true,
    rules: mode === "validate" || lead === "checks", trace: false,
    // 2026-10-14 · the redesign's areas: CPV open when shown; the rest closed until their chip is pressed.
    cpv: true, details: false, clock: true,
    // 2026-10-22 (owner) · always open: the dropdown is where a step's dependency is given, and a
    // folded header ("previous steps") was not found.
    order: true,
  }), [mode, lead, hasArea]);
  const [opened, setOpened] = useState({});
  const isOpen = (id) => (id in opened ? opened[id] : defaults[id]);
  const toggle = (id) => setOpened((o) => ({ ...o, [id]: !isOpen(id) }));
  // 2026-10-14 (step panel redesign) · explanations and generated text behind "Show help",
  // remembered per viewer; the signature's advanced choices; the CPV area opened before anything
  // in it is claimed.
  // 2026-10-14 (owner) · no "Show help" switch: each section's explanation is an ⓘ at the right
  // end of its heading. Inline help (HelpContext) stays off.
  const help = false;
  const [cpvOpen, setCpvOpen] = useState(false);
  // 2026-10-29 (owner) · the "Add a signature" popup: "first" / "second" while open, and the meaning picked in it
  const [addSign, setAddSign] = useState(null);
  const [addMeaning, setAddMeaning] = useState(null);
  // 2026-10-28 (owner) · the optional fields this viewer switched off (floating toolbar → Show / hide fields).
  // A field the step requires is never hidden: Save would be refused for a field nobody can see.
  const { shown: fieldShown } = useFieldPrefs();

  if (!step) return null;
  const isIng = INGREDIENT_STEP_TYPES.includes(step.step_type);
  const isTransfer = step.step_type === "TRANSFER";     // 2026-10-29 (owner) · its own Ingredient settings section
  // 2026-10-13 (owner) · a DISPENSING step dispenses the bill of materials as it stands: no material
  // picker and no ingredient settings — the BOM list and the quantity to charge, and its instruction.
  const isDispensing = step.step_type === "DISPENSING";
  const isCleaning = step.step_type === "CLEANING";
  // 2026-10-07 · a recorded parameter is laid out like a charge: the picker in "What the operator
  // sees", Monitoring and Instruction as sections of their own, the rest folded at the end.
  const isRec = !isIng && (vocab.shapes || {})[step?.step_type]?.lead === "records";
  // A recorded parameter (not a sample) shows its limits and readings first, then what is collected.
  const recordsMonitoring = isRec && step?.step_type !== "SAMPLE";
  const set = (patch) => !readOnly && onChange?.(patch);
  const second = SECOND.includes(meaningOf(step, signLabels));
  const signerChosen = !!((step.signed_by_id ?? step.signature_role_id) || step.signature_policy);
  const labelOptions = (signLabels.length ? signLabels : signMeanings)
    .filter((l) => !l.step_types?.length || l.step_types.includes(step.step_type));
  const bound = step.rules || [];
  const detail = step.detail || [];
  const facts = factsOf(step);
  // A placed row's range, frequency and out-of-range class are edited in the monitoring
  // block; repeating them as read-only facts underneath would be the same fact twice.
  const shownFacts = step.monitoring ? facts.filter((d) => !MON_OWNED_FACTS.has(d.label)) : facts;
  const control = detail.find((d) => d.identity && d.label === "Control")?.value;
  const cpp = step.cpp_edges || [];
  const cppCodes = cpp.map((e) => e.code);
  // The shape a kind of step takes (served vocabulary, one owner): what it leads with and
  // which fields it never carries. A readiness check leads with its checks; a measurement
  // with what it records; a document with its evidence; a signature with who signs.
  const shape = (vocab.shapes || {})[step.step_type] || { lead: "instruction", sees: "What the operator sees", hide: [] };
  // The parameter as the envelope carries it, or — for a pick not yet saved — from the master list.
  const pm = step.parameter && step.parameter.id === step.parameter_id
    ? step.parameter : (masters.parameters || []).find((p) => p.id === step.parameter_id) || null;
  const hidden = new Set(shape.hide || []);
  const paramOff = !fieldShown("panel.parameter") && step.step_type !== "PARAMETER_RECORD";
  const limits = bound.filter((r) => r.min_value != null || r.max_value != null || r.min_duration != null || r.max_duration != null);
  const checks = bound.filter((r) => !limits.includes(r));
  const limitText = (r) => {
    const lo = r.min_value != null ? `≥ ${r.min_value}` : null, hi = r.max_value != null ? `≤ ${r.max_value}` : null;
    const t = [lo, hi].filter(Boolean).join(" and ");
    return `${r.name}${t ? ` · ${t}${r.unit ? ` ${r.unit}` : ""}` : ""}`;
  };
  const source = step.payload?.authored_from;

  const seesBadge = [
    step.instruction && "instruction",
    step.checklist && "checklist",
    (control || step.parameter_id) && "parameter",
    facts.length && !control && `${facts.length} values`,
  ].filter(Boolean).join(" · ") || "nothing yet";

  // 2026-10-06 · on a material step these are the optional extras — the instruction, a parameter
  // it records, the fields the operator fills, the CPP claim. They fold into one section at the
  // end, so the material, its settings, the files and the place come first.
  const instructionField = (
    // 2026-10-28 (owner) · "Instruction" on every step type — a check read "How the check is made"
    // and a document "What is produced" for the same field.
    <Field label="Instruction" required={!readOnly}>
      <Input.TextArea size="small" autoSize={{ minRows: 2, maxRows: vocab.limits?.instruction_rows }}
                      placeholder={readOnly ? "Not written" : "What the operator does"} defaultValue={step.instruction}
                      key={`i-${step.code}`} disabled={readOnly}
                      status={showErrors && !readOnly && !String(step.instruction || "").trim() ? "error" : undefined}
                      onChange={(e) => set({ instruction: e.target.value })} />
      {showErrors && !readOnly && !String(step.instruction || "").trim() ? (
        <span className="u0-fielderr" role="alert">Write what the operator does — the step cannot be saved without it.</span>
      ) : null}
    </Field>
  );
  // The step's middle, in three parts so a structured step (a charge, a recorded parameter) can
  // show the picker where it is chosen and fold the rest into the section at the end.
  const recordsField = (
    <>
          {hidden.has("records") || !fieldShown("panel.records") ? null : (
          <Field label="What it records"
                 hint="In the record's words — 'Inspection · Defect count'. A loop's merge is read off this: what the steps after the loop record is what it aggregates.">
            <Input size="small" defaultValue={step.records} key={`rec-${step.code}`} disabled={readOnly}
                   placeholder="nothing written to the record"
                   onBlur={(e) => e.target.value !== (step.records || "") && set({ records: e.target.value })} />
          </Field>
          )}
    </>
  );
  // 2026-10-21 (owner) · the checklist, on every step type — one field, drawn once: with the parameter on
  // a record or a check step, and on its own on a material step (whose form is its ingredient settings).
  const checklistField = hidden.has("checklist") || !fieldShown("panel.checklist") ? null : (
            <Field label="Checklist" hint="Picked from the library, not typed — a checklist is a ChecklistField the operator works through.">
              <Select size="small" value={step.checklist_id || undefined} disabled={readOnly} allowClear showSearch optionFilterProp="label"
                      placeholder="— none —" onChange={(v) => set({ checklist_id: v || null })}
                      options={[
                        // The bound list first, even when the library list loaded before it existed —
                        // a select that shows a bare id has lost the name it should carry.
                        ...(step.checklist_id && !checklists.some((c) => c.id === step.checklist_id) ? [{ value: step.checklist_id, label: step.checklist || `list ${step.checklist_id}` }] : []),
                        ...checklists.map((c) => ({ value: c.id, label: c.name })),
                      ]} />
              {step.checklist_id ? (
                <ChecklistEditor step={step} readOnly={readOnly} ops={checklistOps} library={checklists} />
              ) : !readOnly && checklistOps ? (
                <NewListRow onCreate={async (name) => { const id = await checklistOps.createList(name); if (id) set({ checklist_id: id }); }} />
              ) : null}
            </Field>
  );
  // The CPP claim — which quality attributes this step moves. In the redesign it lives under
  // "Track in CPV"; the input and what it writes ({cpp: [codes]}) are today's.
  const cppField = hidden.has("cpp") && !cpp.length ? null : (
          <Field label="Quality attributes it moves"
                 hint="Ticking one makes this step a CPP and draws an edge in the criticality graph. Nothing ticked means the parameter is recorded but not claimed to control anything.">
            {cqas.length && !readOnly ? (
              <div className="u0-cqabox">
                {cqas.map((q) => (
                  <label key={q.code} className="u0-cqa">
                    <input type="checkbox" checked={cppCodes.includes(q.code)}
                           onChange={(e) => set({ cpp: e.target.checked ? [...cppCodes, q.code] : cppCodes.filter((c) => c !== q.code) })} />
                    <span>{q.name}</span>
                    <span className="sp">{q.specification}</span>
                  </label>
                ))}
              </div>
            ) : (
              <CriticalityEdges edges={cpp}
                emptyNote={cqas.length ? "Nothing ticked: the parameter is recorded but not claimed to control anything."
                                       : "No quality attributes are recorded for this product, so nothing can be claimed here."} />
            )}
          </Field>
  );
  // 2026-10-28 · the monitoring the form shows once a parameter is chosen, on every step type (its defaults until changed).
  const mon = step.parameter_id && step.step_type !== "SAMPLE" ? monitoringOf(step) : (step.monitoring || null);
  const tracked = cpvOn(step, mon);
  const cpvApplies = step.kind === "STEP" && !(hidden.has("cpp") && !cpp.length);
  const unit = pm?.unit || pm?.unit_name || "";
  const rangeText = mon && (mon.min != null || mon.max != null)
    ? `${mon.min ?? "—"}–${mon.max ?? "—"}${unit ? ` ${unit}` : ""}` : null;
  // 2026-10-28 (owner) · Track in CPV hangs off the parameter: offered right under it, once one is chosen
  // (CPV trends the parameter's readings). A step already tracked keeps it in view so it can be switched off.
  const cpvBlock = cpvApplies && (step.parameter_id || control || tracked) ? (
    <div className="u0-cpvparam">
      <div className="u0-cpvrow">
        <span className="t">Track in CPV</span>
        <Switch size="small" checked={tracked || cpvOpen} disabled={readOnly}
                onChange={(v) => {
                  if (v) { setCpvOpen(true); return; }
                  setCpvOpen(false);
                  const off = cpvOffPatch(step, mon);
                  if (Object.keys(off).length) set(off);
                }} />
        <span className="v">{tracked || cpvOpen ? "On" : "Off"}</span>
        <InfoTip label="About Track in CPV" text="On when the step is a critical process parameter or moves a quality attribute; CPV then trends it batch to batch." />
      </div>
      {tracked || cpvOpen ? (
        <div className="u0-cpvbody">
          {mon ? (
            <Field label="Classification">
              <label className="u0-ingsw" style={{ maxWidth: 360 }}>
                <span>CPP · critical process parameter</span>
                <Switch size="small" disabled={readOnly} checked={!!mon.critical} checkedChildren="Yes" unCheckedChildren="No"
                        onChange={(v) => set({ monitoring: { ...mon, critical: v } })} />
              </label>
            </Field>
          ) : null}
          {cppField}
          <div className="u0-kv"><span className="k">Limits used by CPV</span>
            <span className="v">{rangeText ? `From this step · ${rangeText}` : "—"}</span></div>
        </div>
      ) : null}
    </div>
  ) : null;
  const pickers = (
    <>
          {/* 2026-10-21 (owner) · a thin rule between the checklist, the parameter and the fields, so each
              reads as its own block (surface.css .u0-sepgroup / .u0-sepbefore). */}
          <div className="u0-grid2 u0-sepgroup">
            {checklistField}
            {/* 2026-10-07 · a sample records no parameter: what is drawn is said by its sampling. */}
            {/* 2026-10-14 (owner) · a calculation is what its formula works out, not a parameter
                reading: no parameter under the formula. One an older record already names is
                still shown, so it can be cleared. */}
            {paramOff || (hidden.has("parameter") && !control && !step.parameter) || step.step_type === "SAMPLE"
              || (step.step_type === "CALCULATION" && !step.parameter_id && !control) ? null : (
            <Field label="Parameter" required={!readOnly && step.step_type === "PARAMETER_RECORD"}
                   hint="Picked from the parameter master, never typed: the master carries the data type and unit a reading is checked against at execution.">
              {control ? (
                <div className="u0-bound">{control}</div>
              ) : (
                <Select size="small" value={step.parameter_id ?? undefined} disabled={readOnly} allowClear showSearch optionFilterProp="label"
                        placeholder="— none —" onChange={(v) => set({ parameter_id: v || null })}
                        status={showErrors && step.step_type === "PARAMETER_RECORD" && !step.parameter_id ? "error" : undefined}
                        options={groupedParameters(masters.parameters || [])} />
              )}
              {pm && !recordsMonitoring ? (
                <ParameterCard pm={pm} step={step} limits={limits} cpp={cpp} />
              ) : !pm && shape.lead === "records" && !control ? (
                <Text type="warning" style={{ fontSize: "calc(11.5px * var(--dz-fs, 1))" }}>Name the parameter, or a reading here has no type to be checked against.</Text>
              ) : null}
              {!readOnly && !control && onCreateParameter ? (
                <NewParameterRow categories={masters.categories || []} units={masters.units || []} vocab={vocab}
                                 onCreate={async (body) => { const id = await onCreateParameter(body); if (id) set({ parameter_id: id }); }} />
              ) : null}
            </Field>
            )}
          </div>
          {paramOff ? null : cpvBlock}
          {/* The sample itself — what is drawn, how much, where it goes, what it is tested for —
              in "What is sampled", beside its checklist (services/recipe/sampling.py). */}
          {step.step_type === "SAMPLE" ? (
            <SamplingSettings step={step} readOnly={readOnly} onStage={set} />
          ) : null}
          {/* Full width, under the two pickers: the block has six controls and a narrow pane
              cannot hold it in half a row. */}
          {shape.lead === "records" && step.step_type !== "SAMPLE" ? (
            /* 2026-10-07 · a recorded parameter's limits and readings sit right under the parameter,
               in "What is recorded" — not in a section of their own. Shown with the Monitoring
               dialog's defaults until one is changed (services/recipe/monitoring.py). */
            !paramOff && step.parameter_id ? (
              <>
                <MonitoringEditor showErrors={showErrors} step={{ ...step, monitoring: monitoringOf(step) }} readOnly={readOnly} vocab={vocab}
                                  onStage={set} heading={false} split unitHint={pm?.unit || ""} />
                {/* "What is collected" (the master's facts) below the limits and readings. */}

              </>
            ) : null
          ) : shape.lead === "records" ? null : shape.lead === "equipment" || step.step_type === "CLEANING" ? (
            /* An equipment setpoint is "set a value and confirm it was reached": its range is
               stated here like a recorded parameter's — the server takes both (monitoring.py).
               2026-10-21 (owner) · the monitoring opens the moment a parameter is chosen (with the
               dialog's defaults until one is changed), and there is none while no parameter is —
               no "+ say how it is monitored" to press first. */
            !paramOff && step.parameter_id ? (
              <MonitoringEditor showErrors={showErrors} step={{ ...step, monitoring: monitoringOf(step) }} readOnly={readOnly} vocab={vocab}
                                onStage={set} />
            ) : null
          ) : !paramOff && step.parameter_id ? (
            /* 2026-10-21 (owner) · "parameter select hoga tabhi ye form dikhega": how it is monitored is
               shown only once a parameter is chosen — on every step type.
               2026-10-28 (owner) · and it opens the moment one is, with the dialog's defaults, as on an
               equipment step: it waited for a monitoring the step could not have yet, so a parameter
               picked on an inspection or a document showed no form (the server takes it on every
               type now — monitoring.monitored_types). */
            <MonitoringEditor showErrors={showErrors} step={{ ...step, monitoring: monitoringOf(step) }} readOnly={readOnly} vocab={vocab}
                              onStage={set} />
          ) : null}
    </>
  );
  const detailTail = (
    <>
          {hidden.has("fields") || !fieldShown("panel.fields") ? null : (
            <div className="u0-sepbefore">
              <CollectedFields step={step} readOnly={readOnly} masters={masters} fieldOps={fieldOps} vocab={vocab} lead={shape.lead} onPendingRow={onPendingField} />
            </div>
          )}
          {shownFacts.length && shape.lead !== "material" ? (
            <div className="u0-facts">
              {shownFacts.map((d) => (
                <div key={d.label} className="u0-kv"><span className="k">{d.label}</span><span className="v">{d.value}</span></div>
              ))}
            </div>
          ) : null}
    </>
  );
  // 2026-10-28 (owner) · the instruction is never folded in here: every step type has it in its own
  // "Instruction" section (below), where a charge and a recorded parameter already had it.
  const details = (
    <>
          {/* A recorded parameter leads with its parameter and readings; the others with what they record. */}
          {isRec ? null : recordsField}
          {pickers}
          {isRec ? recordsField : null}
          {detailTail}
    </>
  );

  // ── 2026-10-14 · the step panel redesign (design document "Step panel: current and proposed") ──
  const hasLabels = signLabels.length > 0;
  const meaningOfOpt = (o) => o.meaning || o.value;
  const secondOptions = labelOptions.filter((o) => SECOND.includes(meaningOfOpt(o)));
  const signValue = hasLabels ? labelOf(step, signLabels) : (step.signature_meaning || "NONE");
  const secondWords = (secondOptions.find((o) => o.value === signValue) || {}).label || "Second signer";
  // 2026-10-29 (owner) · who signs with an e-signature — the server's resolved switches (tree.esign_by)
  // 2026-10-29 (owner) · the step-type dropdown lists only the types the operation type allows (as the add-step
  // picker does), with the step's own type kept so a step already of another type still reads its type. Nothing
  // declared on the type: every type, as before. In-Process Check only where the step already is one.
  const allowedTypes = allowedStepTypes ?? operation?.type_ref?.step_types;
  const offerType = (v) => v === step.step_type
    || (v !== "IN_PROCESS_CHECK" && (!allowedTypes?.length || allowedTypes.includes(v)));
  const esignBy = { performer: meaningOf(step, signLabels) === "PERFORMED_BY", first: true, second: true, ...(step.esign_by || {}) };
  // 2026-10-29 (owner) · the extra signatures after the first (tree `signers`), and the names the popup offers:
  // the configured signature labels that need a second person, for this step type (Checked by, Verified by …).
  const moreSigners = Array.isArray(step.signers) ? step.signers : [];
  const sendSigner = (sg) => ({ meaning: sg.meaning, label: sg.label, role_id: sg.role_id ?? null, esign: sg.esign !== false });
  const setMore = (i, patch) => set({ signers: moreSigners.map((sg, j) => sendSigner(j === i ? { ...sg, ...patch } : sg)) });
  const signChoices = (secondOptions.length
    ? secondOptions.filter((o) => !o.step_types?.length || o.step_types.includes(step.step_type))
      .map((o) => ({ value: o.value, label: o.label, meaning: meaningOfOpt(o) }))
    : SECOND.map((m) => ({ value: m, label: SIGN_TEXT[m], meaning: m })));
  const areaRequired = step.kind === "STEP" && (areaList || []).length > 0;
  const clockRules = bound.filter((r) => r.rule_type === "CLOCK");
  const docsCount = (step.attachments || []).length + (step.sops || []).length;
  const openArea = (id) => setOpened((o) => ({ ...o, [id]: true }));
  const areaHelp = {
    runs: "The room sets which equipment can be chosen. The area and equipment chosen here are set on this step and every step after it in the operation.",
    rules: bound.length
      ? "The step is authored. The value is not — it belongs to the rule, and the rule is what the engine evaluates at execution."
      : "With no rule, the step is performed and recorded but nothing about it can be judged — a gap for any step that has a limit in the SOP.",
    trace: "Where this step came from and the code it keeps for ever.",
  };

  // The occasional areas: one row of chips, each opening its area in place. Not shown where the
  // step type has nothing of the kind.
  const chips = [
    isIng && !isDispensing && !isTransfer ? { id: "ingredient", label: "Ingredient settings", has: Object.keys(step.ingredient || {}).length > 0,
      value: Object.keys(step.ingredient || {}).length ? `${Object.keys(step.ingredient).length} set` : null } : null,
    { id: "rules", label: "Limits & rules", has: bound.length > 0, value: bound.length ? String(bound.length) : null },
    shape.lead === "evidence" ? null : { id: "docs", label: "Images & procedures", has: docsCount > 0, value: docsCount ? String(docsCount) : null },
    { id: "trace", label: "History", has: true, value: source ? `from ${String(source).replace(/_/g, " ")}` : "authored here" },
  ].filter(Boolean);

  const areaBody = {
    runs: (
      <>
        {area?.value ? (
          <Flex vertical gap={4} style={{ marginBottom: 10 }} className="u0-gradeline">
            <span className="u0-eyebrow">Cleanroom grade</span>
            <Text style={{ fontSize: "calc(13px * var(--dz-fs, 1))" }}>
              {gradeLabel(area.value)}
              <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))", marginLeft: 6 }}>
                {step.area_grade ? "stated on this step" : `carried from ${area.source}`}
              </Text>
            </Text>
          </Flex>
        ) : null}
        <Flex vertical gap={4} style={{ marginBottom: 10 }}>
          <span className="u0-eyebrow">May run in{areaRequired ? <span className="u0-req" aria-label="required"> *</span> : null}</span>
          {/* 2026-10-28 (owner) · every chosen area by name, wrapping — "maxTagCount: responsive" collapsed a
              long name to "+ 1 …", and on a read-only (finalized) record nobody could see what was chosen. */}
          <Cascader
            size="small" allowClear disabled={readOnly} multiple
            showCheckedStrategy={Cascader.SHOW_PARENT}
            status={showErrors && !(shownAreaPaths || []).length && (areaList || []).length && !readOnly ? "error" : undefined}
            style={{ width: "100%" }} placeholder={readOnly ? "—" : "area not stated"}
            expandTrigger="hover"
            value={shownAreaPaths}
            options={areaTree}
            onChange={(paths) => {
              const place = placeFromPaths(paths);
              setPendingAreas(place.area_ids);
              setPendingBooths(place.booth_ids);
              onChange(place);
            }}
          />
          {showErrors && !readOnly && step.kind === "STEP" && !(shownAreaPaths || []).length && (areaList || []).length ? (
            <span className="u0-fielderr" role="alert">Select where this step is performed — the step cannot be saved without it.</span>
          ) : null}
          <HelpText>The room sets which equipment can be chosen. The area and equipment chosen here are set on this step and every step after it in the operation.</HelpText>
        </Flex>
        {step.kind === "STEP" && (isDispensing || isCleaning || fieldShown("panel.equipment")) ? (
          <Flex vertical gap={4} style={{ marginBottom: 10 }}>
            {/* 2026-10-22 (owner) · a dispense step names the balance it is weighed on — required there. */}
            <span className="u0-eyebrow">{isDispensing ? "Equipment — the balance" : isCleaning ? "Equipment — what is cleaned" : "Equipment"}
              {isDispensing || isCleaning ? (!readOnly ? <span className="u0-req" aria-label="required"> *</span> : null)
                : <span style={{ textTransform: "none", fontWeight: 400 }}> (optional)</span>}</span>
            <EquipmentPicker step={step} areaList={areaList} readOnly={readOnly} onPick={(ids) => onChange({ booth_ids: ids })} />
            {showErrors && (isDispensing || isCleaning) && !readOnly && !(step.booth_ids ?? (step.booths || []).map((b) => b.id)).length ? (
              <span className="u0-fielderr" role="alert">
                {isDispensing ? "Choose the balance this step is weighed on" : "Choose the equipment this step cleans"} — the step cannot be saved without it.
              </span>
            ) : null}
          </Flex>
        ) : null}
        {cpp.length ? (
          <HelpText>{`This step controls ${cpp.length} declared attribute${cpp.length === 1 ? "" : "s"}. Repetition and concurrency belong to the operation and are shown there.`}</HelpText>
        ) : null}
      </>
    ),
    ingredient: isIng && !isDispensing ? (
      <IngredientSettings step={step} readOnly={readOnly} onChange={set}
                          bomLine={(bomLines || []).find((l) => l.sap_code === step.material_sap_code)} />
    ) : null,
    rules: (
      <>
        {isIng && !isDispensing && !isTransfer ? <IngredientSettings step={step} readOnly={readOnly} onChange={set} part="rules" /> : null}
        <MonitoringRules step={step.step_type === "SAMPLE" ? { ...step, monitoring: null }
                               : shape.lead === "records" && step.parameter_id ? { ...step, monitoring: monitoringOf(step) } : step}
                         readOnly={readOnly} onStage={set} hideCritical={cpvApplies} />
        {bound.length ? (
          <Flex vertical gap={6}>
            {bound.map((r) => (
              <RuleRow key={r.binding || r.id} rule={r} readOnly={readOnly} onTighten={onTighten} onUnbind={onUnbind} onRefresh={onRefresh} />
            ))}
            <HelpText>The step is authored. The value is not — it belongs to the rule, and the rule is what the engine evaluates at execution.</HelpText>
          </Flex>
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: "calc(13px * var(--dz-fs, 1))" }}>No limits or rules on this step.</Text>
            <HelpText>It will be performed and recorded, and nothing about it can be judged. Legitimate for a purely procedural step — a gap for any step that has a limit in the SOP.</HelpText>
          </>
        )}
        {!readOnly && onBind ? <BindRule rules={rules} bound={bound} onBind={onBind}
                                         operations={operations} operationCode={operationCode} /> : null}
      </>
    ),
    docs: (
      <StepAttachments step={step} readOnly={readOnly || !files} limits={vocab.limits || {}}
                       onUpload={files ? (file, meta) => files.onUpload(step.code, file, meta) : undefined}
                       onRemoveAttachment={files ? (id) => files.onRemove(step.code, id) : undefined}
                       onLinkSop={files ? (body) => files.onLinkSop(step.code, body) : undefined}
                       onUnlinkSop={files ? (linkId, reason) => files.onUnlinkSop(step.code, linkId, reason) : undefined}
                       searchSops={files?.searchSops} />
    ),
    clock: step.kind === "STEP" && (onBind || readOnly) ? (
      <StepDuration bound={bound} readOnly={readOnly} onBind={onBind} onTighten={onTighten} onUnbind={onUnbind} />
    ) : null,
    trace: (
      <Flex vertical gap={4}>
        <div className="u0-kv"><span className="k">Stable code</span>
          <span className="v"><span className="dz-code">{step.code}</span> <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>assigned once · never reused</Text></span></div>
        {source ? (
          <div className="u0-kv"><span className="k">Authored from</span>
            <span className="v">{source} row {step.payload?.source_id}</span></div>
        ) : (
          <div className="u0-kv"><span className="k">Authored</span><span className="v">in the designer</span></div>
        )}
        {/* 2026-10-14 · this step's own rules — it listed the whole library before. */}
        <div className="u0-kv"><span className="k">Bound rules</span>
          <span className="v">{bound.length ? bound.map((r) => r.name).join(" · ") : "none"}</span></div>
        {facts.length ? (
          <div className="u0-kv"><span className="k">Free values</span>
            <span className="v">{facts.length} recorded, never checked</span></div>
        ) : null}
      </Flex>
    ),
  };

  const peopleAdds = readOnly ? [] : [
    !second ? { key: "checker", label: "Another signature", onClick: () => setAddSign("first") }
      : 1 + (Array.isArray(step.signers) ? step.signers.length : 0) < (vocab.sign_max || MAX_SIGNERS)
        ? { key: "checker2", label: "Another signature", onClick: () => setAddSign("more") } : null,
  ];
  const nothingYet = seesBadge === "nothing yet";

  // 2026-10-14 (owner) · a parameter record shows no material block; a material it cites stays on the record.
  const materialBlock = step.step_type !== "PARAMETER_RECORD" && step.step_type !== "CALCULATION"
          && (shape.lead === "material" || step.material_sap_code) ? (
          <div className="u0-lead">
            <span className="dz-eyebrow">{isTransfer ? "RM" : "The material"}{!inLoop && !isDispensing && !readOnly ? <span className="u0-req" aria-label="required"> *</span> : null}</span>
            {isDispensing ? (
              <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
                {inLoop ? "From the bill of materials — one material per pass." : "From the bill of materials."}
              </Text>
            ) : <Select
              size="small" style={{ width: "100%" }} allowClear showSearch disabled={readOnly}
              placeholder={inLoop
                ? "from the collection at issue — one per iteration"
                : "not stated — which material does this step charge?"}
              value={step.material_sap_code || soleMaterial || undefined}
              optionFilterProp="label"
              onChange={(v) => onChange({ material_sap_code: v || "",
                                          material_provenance: v ? "CONFIRMED" : "" })}
              options={materialOptions}
              notFoundContent={(bomLines || []).length
                ? "No material of this operation's dispensing lane is on the bill of materials."
                : "This record has no bill of materials yet — bring it from the MFR in the Bill of materials panel."}
              optionRender={({ data }) => (
                <div className="u0-matopt">
                  <span className="n">{data.label}</span>
                  {data.render ? <span className="q">{data.render}</span> : null}
                </div>
              )}
            />}
            {(() => {
              if (!INGREDIENT_STEP_TYPES.includes(step.step_type)) return null;
              const small = { fontSize: "calc(12px * var(--dz-fs, 1))" };
              const box = (children) => (
                <Flex vertical gap={2} style={{ marginTop: 8 }} data-testid="charge-quantity">
                  <span className="u0-eyebrow">Quantity to charge</span>
                  {children}
                </Flex>
              );
              const sap = step.material_sap_code;
              if (inLoop && !sap) {
                const dep = (derived[`node:${step.code}#membership`]?.dependencies || [])[0] || "";
                const loopNode = nodesByCode[String(dep).replace(/^node:/, "")];
                const passes = (loopNode?.subjects || []).map((sub) => {
                  const l = (bomLines || []).find((b) => b.sap_code === sub.sap_code);
                  const q = !l ? "" : (l.qs || l.total_value == null) ? "q.s." : fmtQty(l.total_value);
                  return { label: sub.label, sap: sub.sap_code || "", q, unit: l && !l.qs ? (l.unit || "") : "" };
                });
                // 2026-10-13 · numbered, one row per material: what, its SAP code, and how much.
                return box(passes.length ? (<>
                  <Text type="secondary" style={small}>
                    Each pass dispenses that material's full quantity on the bill · {passes.length} material{passes.length === 1 ? "" : "s"}
                  </Text>
                  <ol className="u0-bomlist">
                    {passes.map((x, i) => (
                      <li key={`${x.sap}-${x.label}`}>
                        <span className="no">{i + 1}</span>
                        <span className="nm">
                          <span className="t">{x.label}</span>
                          {x.sap ? <span className="sap">{x.sap}</span> : null}
                        </span>
                        <span className="q">{x.q || "—"}{x.unit ? <span className="u"> {x.unit}</span> : null}</span>
                      </li>
                    ))}
                  </ol>
                </>) : (<>
                  <Input size="small" disabled value="Recorded at execution, per pass" />
                  <Text type="secondary" style={small}>This repeat is not over bill-of-materials lines{loopNode?.payload?.over ? ` (it repeats per ${loopNode.payload.over})` : ""}, so the amount of each pass is recorded on the floor.</Text>
                </>));
              }
              const line = sap ? (bomLines || []).find((l) => l.sap_code === sap) : null;
              if (!line) return null;
              if (line.qs || line.total_value == null) {
                return box(<>
                  <Input size="small" disabled value={`q.s.${line.qs_target ? ` — ${line.qs_target}` : ""}${line.unit ? ` (${line.unit})` : ""}`} />
                  <Text type="secondary" style={small}>Quantity sufficient — as much as the step needs; the amount used is recorded at execution.</Text>
                </>);
              }
              if (inLoop) return null;
              const total = Number(line.total_value);
              const whole = step.charge_quantity === undefined || step.charge_quantity === null || step.charge_quantity === "";
              const own = whole ? total : Number(step.charge_quantity);
              const left = Number(line.remaining_value ?? line.total_value) + (props_saved_share(step) || 0);
              const over = !whole && own > left + 1e-9;
              return box(<>
                <InputNumber size="small" min={0} step={0.001} disabled={readOnly} style={{ width: "100%" }}
                             value={own} addonAfter={line.unit || undefined} status={over ? "error" : undefined}
                             onChange={(v) => onChange({ charge_quantity: v == null || Math.abs(Number(v) - total) < 1e-9 ? "" : v })} />
                <Text type={over ? "danger" : "secondary"} style={small}>
                  {whole ? `Whole line — ${fmtQty(total)} ${line.unit || ""} on the bill` : `Part of the line — ${fmtQty(own)} of ${fmtQty(total)} ${line.unit || ""}`}
                  {" · "}{fmtQty(left)} {line.unit} available for this step
                </Text>
              </>);
            })()}
            {step.material_provenance ? (
              <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))" }}>
                {step.material_provenance === "CITED" ? "Cited from the record." : "Confirmed by a person."}
              </Text>
            ) : soleMaterial && !isDispensing ? (
              <Text type="secondary" style={{ fontSize: "calc(12px * var(--dz-fs, 1))", color: "var(--dz-warn)" }}>
                The only material in this lane — filled in for you. Press Save to record it.
              </Text>
            ) : null}
            {facts.map((d) => <div key={d.label} className="u0-kv"><span className="k">{d.label}</span><span className="v">{d.value}</span></div>)}
          </div>
        ) : null;

  return (
    <HelpContext.Provider value={help}>
    <Flex vertical gap={0} className={`u0-stepform u0-p2 ${readOnly ? "u0-ro" : ""}`}>
      {blastRadius ? <Alert type="warning" showIcon message={blastRadius} /> : null}

      {/* What the step is, in plain words: its kind, the record's state, the help switch; then its
          name, and where it sits. The code is a help fact, not a header. */}
      {/* 2026-10-28 (owner) · the Save line is the card's first row, not inside its header, so it
          can stay pinned at the top of the pane while the step's fields scroll under it. */}
      {saveBar}
      <div className="u0-stepid" data-family={familyOf(vocab, step.step_type || step.kind)} data-shape={shape.lead}>
        <div className="u0-stephead">
          {readOnly || step.kind !== "STEP" ? (
            <span className="u0-typeword">{typeWords(step.step_type, stepTypes)}</span>
          ) : (
            /* 2026-10-21 (owner) · the list opens as wide as its names (it opened as wide as the word
               beside it, and read "B…", "Tr…", "Cl…"), and can be searched. */
            <Select size="small" className="u0-typepick" style={{ minWidth: 220 }}
                    popupMatchSelectWidth={false} dropdownStyle={{ minWidth: 300 }} listHeight={440}
                    showSearch optionFilterProp="search"
                    value={step.step_type} onChange={(v) => set({ step_type: v })} placeholder="Kind of step"
                    options={stepTypeGroups(vocab, stepTypes, offerType)}
                    optionRender={(o) => (
                      <span style={{ display: "inline-flex", gap: 8, alignItems: "baseline" }}>
                        {o.data.code ? <code style={{ fontSize: 12, color: "var(--dz-faint, #64748b)", minWidth: 30 }}>{o.data.code}</code> : null}
                        <span>{o.data.label}</span>
                      </span>
                    )} />
          )}

          <span className={`u0-statebadge ${readOnly ? "ro" : "rw"}`}>
            {[recordBadge, readOnly ? "read-only" : "editable"].filter(Boolean).join(" · ")}
          </span>
          <span style={{ flex: 1 }} />
          <InfoTip label="About this step" text={`Code ${step.code} — assigned once, never reused.`} />
        </div>
        <input className="u0-stepid-title" placeholder="What this step is called"
               defaultValue={step.title} key={`t-${step.code}`} disabled={readOnly}
               aria-invalid={!step.title}
               onChange={(e) => set({ title: e.target.value })} />
        {/* 2026-10-28 (owner) · no "Step 1 · <operation>" line under the name: the list in the
            middle already numbers the step and names its operation. */}
      </div>

      {/* 2026-10-28 (owner) · the instruction first, above who performs it. */}
      <Section id="instruction" title="Instruction" className="u0-instrsec"
               open={isOpen("instruction")} onToggle={toggle}
               adds={readOnly || shape.lead === "evidence" ? [] : [{ key: "media", label: "Image or procedure (SOP)", onClick: () => openArea("docs") }]}
               help="What the operator reads on the tablet before recording.">
        {instructionField}
      </Section>

      {/* People: performed by, then one level at a time. The data holds one second signature —
          its meaning and who may give it — so the second level is that signature (owner's choice:
          the verifier level is drawn only once the record can hold it). */}
      <Section id="basic" title="Who performs" open={isOpen("basic")} onToggle={toggle} adds={peopleAdds}
               help="Who records the reading, what their signature means, and who must give the second signature.">
        <div className="u0-people">
          <Field label="Performed By" required={showRole && !readOnly}>
            {showRole ? (
              <>
                <div className="u0-signerrow">
                  <Select size="small" value={(step.performed_by_id ?? step.role_id) ?? undefined} disabled={readOnly}
                          onChange={(v) => set({ performed_by_id: v ?? null })} options={roles}
                          placeholder={readOnly ? "—" : "Who performs this step"} allowClear
                          status={showErrors && !readOnly && (step.performed_by_id ?? step.role_id) == null ? "error" : undefined}
                          showSearch optionFilterProp="label" />
                  <EsignSwitch who="The performer" on={esignBy.performer} readOnly={readOnly}
                               onChange={(v) => set({ esign_by: { ...esignBy, performer: v } })} />
                  {/* the ✕ the signer rows carry, kept as space so every E-sign switch lines up */}
                  {!readOnly ? <span className="u0-x" aria-hidden="true" style={{ visibility: "hidden" }}>✕</span> : null}
                </div>
                {showErrors && !readOnly && (step.performed_by_id ?? step.role_id) == null ? (
                  <span className="u0-fielderr" role="alert">Choose who performs this step — the step cannot be saved without it.</span>
                ) : null}
              </>
            ) : (
              <Text type="secondary" style={{ fontSize: "calc(13px * var(--dz-fs, 1))" }}>the recipe decides</Text>
            )}
          </Field>
          {second ? (
            <div className="u0-signer">
              <Field label={capBy(secondWords)}>
                <div className="u0-signerrow">
                  <Select size="small" value={(step.signed_by_id ?? step.signature_role_id) ?? undefined} disabled={readOnly}
                          onChange={(v) => set({ signed_by_id: v })} options={roles}
                          placeholder={readOnly ? "—" : "Who may give it"}
                          status={signerChosen || readOnly || !showErrors ? undefined : "error"} showSearch optionFilterProp="label" />
                  <EsignSwitch who={secondWords} on={esignBy.first} readOnly={readOnly}
                               onChange={(v) => set({ esign_by: { ...esignBy, first: v } })} />
                  {!readOnly ? (
                    <Tooltip title="Remove the second signature">
                      <button type="button" className="u0-x" aria-label="Remove the second signature"
                              onClick={() => set(removeSecondPatch(hasLabels))}>✕</button>
                    </Tooltip>
                  ) : null}
                </div>
              </Field>
              {!signerChosen && !readOnly ? (
                <Text type="danger" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
                  A second person is required and nothing says who may be one — any user could satisfy this.
                </Text>
              ) : null}
              {/* 2026-10-29 (owner) · the extra signatures after the first — as many as the step needs (up to
                  MAX_SIGNERS), the same kind may repeat; asked on the tablet in the order added (second_signer.py). */}
              {moreSigners.map((sg, i) => (
                <Field key={i} label={capBy(sg.label || SIGN_TEXT[sg.meaning] || "Signature")} required={!readOnly}>
                  <div className="u0-signerrow">
                    <Select size="small" value={sg.role_id ?? undefined} disabled={readOnly}
                            onChange={(v) => setMore(i, { role_id: v ?? null })}
                            options={roles} placeholder={readOnly ? "—" : "Who may give it"} showSearch optionFilterProp="label"
                            status={showErrors && !readOnly && sg.role_id == null ? "error" : undefined} />
                    <EsignSwitch who={capBy(sg.label || SIGN_TEXT[sg.meaning]) || "The signer"} on={sg.esign !== false} readOnly={readOnly}
                                 onChange={(v) => setMore(i, { esign: v })} />
                    {!readOnly ? (
                      <Tooltip title="Remove this signature">
                        <button type="button" className="u0-x" aria-label="Remove this signature"
                                onClick={() => set({ signers: moreSigners.filter((_, j) => j !== i).map(sendSigner) })}>✕</button>
                      </Tooltip>
                    ) : null}
                  </div>
                  {showErrors && !readOnly && sg.role_id == null ? (
                    <span className="u0-fielderr" role="alert">Choose who may give this signature — the step cannot be saved without it.</span>
                  ) : null}
                </Field>
              ))}
              {!readOnly && 1 + moreSigners.length < (vocab.sign_max || MAX_SIGNERS) ? (
                <button type="button" className="u0-addchip" onClick={() => setAddSign("more")}>+ Add another signature</button>
              ) : null}
            </div>
          ) : !readOnly ? (
            <button type="button" className="u0-addchip" onClick={() => setAddSign("first")}>+ Add another signature</button>
          ) : null}
        </div>
        {/* 2026-10-29 (owner) · which signature to add, asked in a popup from the configured names; it becomes the
            row's label. The same one may be added again (a different person signs each). */}
        <Modal open={!!addSign} title="Add a signature" width={380} rootClassName="dz" okText="Add"
               okButtonProps={{ disabled: !addMeaning }} onCancel={() => { setAddSign(null); setAddMeaning(null); }}
               onOk={() => {
                 const pick = signChoices.find((c) => c.value === addMeaning);
                 if (pick) {
                   if (addSign === "first") {
                     set(hasLabels ? { sign_label: pick.value } : { sign_policy: pick.meaning });
                   } else {
                     set({ signers: [...moreSigners.map(sendSigner), { meaning: pick.meaning, label: pick.label, role_id: null, esign: true }] });
                   }
                 }
                 setAddSign(null); setAddMeaning(null);
               }}>
          <p className="dz-fieldsnote">Which signature does this step need? On the tablet they are asked in the order you add them.</p>
          <Radio.Group value={addMeaning} onChange={(e) => setAddMeaning(e.target.value)} className="dz-signpick">
            {signChoices.map((c) => <Radio.Button key={c.value} value={c.value}>{capBy(c.label)}</Radio.Button>)}
          </Radio.Group>
        </Modal>
      </Section>

      {/* 2026-10-14 (owner) · the step's duration stands in the panel for every step type, right under
          "Who performs" (was "What happens"), as it was before the redesign — behind a closed "Clock" chip it read as removed. */}
      {areaBody.clock && fieldShown("panel.duration") ? (
        <Section id="clock" title="Step duration" open={isOpen("clock")} onToggle={toggle}
                 badge={clockRules.length ? clockRules.map((r) => r.name).join(", ") : null}>
          {areaBody.clock}
        </Section>
      ) : null}

      {/* 2026-10-22 (owner) · the steps this step waits for (step_dependency.py); none = the previous steps.
          2026-10-28 (owner) · right after the step's duration, before where it runs. */}
      {step.kind === "STEP" && operation && !step.span && fieldShown("panel.depends") ? (
        <Section id="order" title="Depends on" open={isOpen("order")} onToggle={toggle}
                 badge={(step.after_steps || []).length ? (step.after_steps || []).join(", ") : "previous steps"}
                 help="Which steps must be done before the operator can open this one. Nothing chosen: every step before it, as now. Chosen: only those — steps waiting on the same step open side by side on the tablet.">
          <DependsOnField step={step} operation={operation} readOnly={readOnly} onChange={set} />
        </Section>
      ) : null}

      {/* 2026-10-15 (owner) · where the step runs stands in the panel, not behind a chip: the area
          is required (Save is refused without it), the equipment is optional. A carried-forward
          area is the step's own and counts as chosen. */}
      {step.kind === "STEP" ? (
        <Section id="runs" title={areaRequired ? "Where it runs *" : "Where it runs"}
                 open={isOpen("runs")} onToggle={toggle}
                 badge={(step.areas || []).length ? (step.areas.length === 1 ? (step.areas[0].name || step.areas[0].code) : `${step.areas.length} areas`) : null}
                 help={areaHelp.runs}>
          {areaBody.runs}
        </Section>
      ) : null}

      {/* the steps this step is linked to (step_link.py): it works on their output and runs beside them. Only the
          In-Process Check step of an operation that states an in-process sampling plan is linked — both the plan and its step (the
          server refuses any other step). */}
      {step.kind === "STEP" && operation && !step.span && !!operation.inspection?.ipc_sampling?.bands?.length
        && step.step_type === "IN_PROCESS_CHECK" ? (
        <Section id="link" title="Linked to" open={isOpen("link")} onToggle={toggle}
                 badge={(step.linked_steps || []).length ? (step.linked_steps || []).join(", ") : "not linked"}
                 help="An earlier step whose output this step works on. A linked step opens together with it — the two run side by side on the tablet — unless Depends on says otherwise.">
          <LinkedToField step={step} operation={operation} readOnly={readOnly} onChange={set} />
        </Section>
      ) : null}

      {/* a visual-inspection pass (inspection_pass.py): an Inspection step of a visual-inspection operation is one pass over the tubs. */}
      {step.kind === "STEP" && operation && !step.span && (step.step_type === "INSPECTION" || step.step_type === "VISUAL_INSPECTION") && operation.inspection?.behaviour === "UNIT_COUNT" ? (
        <Section id="pass" title="Inspection pass" open={isOpen("pass")} onToggle={toggle}
                 badge={passByItself(step) ? "one inspection" : step.inspection_pass ? (step.inspection_pass.label || "pass") : "not a pass"}
                 help={step.step_type === "VISUAL_INSPECTION"
                   ? "A Visual Inspection step is one inspection by itself: one step, one pass, named as the step. Add another Visual Inspection step for a second inspection; it takes the units the earlier one accepted. The settings here are optional overrides. A step placed after the passes waits for all of them."
                   : "Make this Inspection step one pass over the tubs. Add one Inspection step for each pass the operation needs: two steps for a first and a second pass, one for a single pass. A step placed after the passes waits for all of them."}>
          <InspectionPassField step={step} operation={operation} readOnly={readOnly} onChange={set} />
        </Section>
      ) : null}

      {/* the AQL of a Sample step of a visual-inspection operation (aql_setup.py): level, the defect groups of the inspection specification with their AQLs, the tray rule. */}
      {aqlSetup && isAqlSampleStep(step, operation) ? (
        <Section id="aql" title="AQL" open={isOpen("aql")} onToggle={toggle}>
          <AqlSetupField step={step} readOnly={readOnly} aql={aqlSetup} defectSet={operation.inspection?.defect_set || null} />
        </Section>
      ) : null}

      {/* 2026-10-29 (owner) · a material step's ingredient settings in a section of their own, ABOVE what it moves —
          and the material it moves at the top of it (it was a chip at the panel's foot, below the material). */}
      {isTransfer ? (
        <Section id="ingredient" title="Ingredient settings" open={isOpen("ingredient")} onToggle={toggle}>
          <IngredientSettings step={step} readOnly={readOnly} onChange={set} showErrors={showErrors}
                              materialSlot={materialBlock}
                              bomLine={(bomLines || []).find((l) => l.sap_code === step.material_sap_code)} />
          {/* 2026-10-29 (owner) · the form fields only — no verification / lot rules on a transfer */}
        </Section>
      ) : null}

      {/* the AQL step of a visual-inspection operation takes its sample, tubs and limits from the AQL section above: what a lab sample would ask (checklist, department, test type…) is not used there */}
      {!isAqlSampleStep(step, operation) ? (
      <Section id="does" title={shape.sees} badge={!readOnly && nothingYet ? "nothing yet" : null}
               tone={!readOnly && nothingYet ? "warn" : ""}
               open={isOpen("does")} onToggle={toggle}
               help={shape.lead === "records" ? (recordsMonitoring && step.parameter_id && monitoringSentence(monitoringOf(step))
                 ? `Why it is collected: ${monitoringSentence(monitoringOf(step))}.`
                 : "What is measured, the limits it is judged against, and how often it is read.") : null}>
        {/* 2026-10-14 (owner) · a calculation works the formula attached here (step_formula.py). */}
        {step.step_type === "CALCULATION" ? <FormulaField step={step} readOnly={readOnly} onChange={set} /> : null}
        {/* 2026-10-19 (owner) · whether this calculation verifies the material's ARs (step_formula.VERIFY_KEY). */}
        {step.step_type === "CALCULATION" ? <VerifyArField step={step} readOnly={readOnly} onChange={set} /> : null}
        {/* 2026-10-21 (owner) · a cleaning step: its type of cleaning and whether its record is attached. */}
        {step.step_type === "CLEANING" ? <CleaningField step={step} readOnly={readOnly} onChange={set} vocab={vocab} /> : null}
        {/* 2026-10-14 (owner) · a dispense charge has no formula: its quantity is the BOM's or a calculation's. */}
        {["MATERIAL_CHARGE", "DISPENSING"].includes(step.step_type)
          ? <QuantityFromField step={step} operation={operation} readOnly={readOnly} onChange={set} /> : null}
        {/* 2026-10-14 (owner) · the material a calculation's formula is worked for. Inside a
            dispensing loop there is nothing to pick — each pass brings its own material, one by
            one; outside a loop the author picks it from the bill of materials. */}
        {step.step_type === "CALCULATION" ? (inLoop ? (() => {
          const dep = (derived[`node:${step.code}#membership`]?.dependencies || [])[0] || "";
          const loopNode = nodesByCode[String(dep).replace(/^node:/, "")];
          const subs = loopNode?.subjects || [];
          return (
            <Field label="Material" hint="From the loop — the formula is worked for each pass's material, one by one.">
              {subs.length ? (
                <ol className="u0-bomlist">
                  {subs.map((sub, i) => (
                    <li key={`${sub.sap_code}-${sub.label}`}>
                      <span className="no">{i + 1}</span>
                      <span className="nm"><span className="t">{sub.label}</span>
                        {sub.sap_code ? <span className="sap">{sub.sap_code}</span> : null}</span>
                      <span className="q">{sub.quantity || "—"}{sub.uom ? <span className="u"> {sub.uom}</span> : null}</span>
                    </li>
                  ))}
                </ol>
              ) : (
                <Text type="secondary" style={{ fontSize: "calc(12.5px * var(--dz-fs, 1))" }}>
                  The loop names no materials yet — tick them on the loop.
                </Text>
              )}
            </Field>
          );
        })() : (
          <Field label="Material" hint="The bill-of-materials line the formula is worked for — its quantity goes into the formula on the tablet. Leave empty for a calculation of no material (a yield, a reconciliation).">
            <Select size="small" style={{ width: "100%" }} allowClear showSearch disabled={readOnly} optionFilterProp="label"
                    placeholder="— no material —" value={step.material_sap_code || undefined}
                    onChange={(v) => set({ material_sap_code: v || "", material_provenance: v ? "CONFIRMED" : "" })}
                    options={(bomLines || []).filter((l) => l.sap_code).map((l) => ({
                      value: l.sap_code, label: `${l.material || l.sap_code} · ${l.sap_code}`,
                      render: l.qs || l.total_value == null ? "q.s." : `${fmtQty(l.total_value)}${l.unit ? ` ${l.unit}` : ""}`,
                    }))}
                    optionRender={({ data }) => (
                      <div className="u0-matopt">
                        <span className="n">{data.label}</span>
                        {data.render ? <span className="q">{data.render}</span> : null}
                      </div>
                    )} />
          </Field>
        )) : null}
        {step.step_type === "CLEARANCE" ? (
          <Field label="Clearance profile" required={!readOnly} hint="Which QA clearance format this step follows — its captures are what the operator answers.">
            <Select size="small" showSearch allowClear optionFilterProp="label" disabled={readOnly}
              style={{ width: "100%" }} placeholder={readOnly ? "—" : "Choose the clearance profile — required"}
              status={showErrors && !step.clearance_profile_id && !readOnly ? "error" : undefined}
              value={step.clearance_profile_id || undefined}
              onChange={(v) => set({ clearance_profile_id: v || null })}
              options={clearanceProfiles.map((p) => ({ value: p.id, label: p.name }))} />
            {(() => {
              const p = clearanceProfiles.find((x) => x.id === step.clearance_profile_id);
              const caps = p?.captures || step.clearance?.captures?.map((c) => c.label) || [];
              return caps.length ? <CaptureList caps={caps} /> : null;
            })()}
          </Field>
        ) : null}
        {/* 2026-10-14 (owner) · shown only when there are checks — an empty notice took a block of space
            the "Limits & rules" chip already offers. */}
        {shape.lead === "checks" && (checks.length || limits.length) ? (
          <div className="u0-lead">
            <span className="dz-eyebrow">Checks made here</span>
            {checks.length || limits.length ? (
              <ul className="u0-checks">
                {checks.map((r) => <li key={r.binding || r.id}><b>{r.name}</b>{r.subject ? <span> · per {r.subject}</span> : null}{r.scope ? <span className="dz-chip">{String(r.scope).toLowerCase()}</span> : null}</li>)}
                {limits.map((r) => <li key={r.binding || r.id}>{limitText(r)}</li>)}
              </ul>
            ) : <Text type="secondary" style={{ fontSize: "calc(13px * var(--dz-fs, 1))" }}>No checks bound yet{readOnly ? "." : " — add them under Limits & rules."}</Text>}
          </div>
        ) : null}
        {(shape.lead === "records" || shape.lead === "equipment" || shape.lead === "hold") && (limits.length || control) ? (
          <div className="u0-lead">
            <span className="dz-eyebrow">{shape.lead === "hold" ? "Held within" : "Judged against"}</span>
            {control ? <div className="u0-kv"><span className="k">Parameter</span><span className="v">{control}</span></div> : null}
            {limits.map((r) => <div key={r.binding || r.id} className="u0-kv"><span className="k">{r.rule_type?.toLowerCase()}</span><span className="v">{limitText(r)}</span></div>)}
            {checks.map((r) => <div key={r.binding || r.id} className="u0-kv"><span className="k">{r.rule_type?.toLowerCase()}</span><span className="v">{r.name}</span></div>)}
          </div>
        ) : null}
        {/* 2026-10-14 (owner) · a dispense charge says how it is weighed — the balance's reading taken
            automatically, or typed by the operator. Required; Manual until changed. Saved where the
            ingredient settings keep it (`ingredient.execution_mode`), which the copy and new version carry. */}
        {isDispensing ? (
          <Field label="Weighing mode" required>
            <Select size="small" disabled={readOnly} data-testid="weighing-mode"
                    value={(step.ingredient || {}).execution_mode || "Manual"}
                    onChange={(v) => set({ ingredient: { ...(step.ingredient || {}), execution_mode: v } })}
                    options={[
                      { value: "Manual", label: "Manual — the operator enters the weight" },
                      { value: "Automatic", label: "Auto — read from the balance" },
                      ...((step.ingredient || {}).execution_mode === "Semi-Automatic" ? [{ value: "Semi-Automatic", label: "Semi-automatic" }] : []),
                    ]} />
          </Field>
        ) : null}
        {/* the material — in Ingredient settings on a transfer (2026-10-29, owner) */}
        {isTransfer ? null : materialBlock}
        {shape.lead === "signature" ? (
          <div className="u0-lead">
            <span className="dz-eyebrow">The signature</span>
            <div className="u0-kv"><span className="k">Meaning</span><span className="v">{step.signature || "none — this step signs nothing"}</span></div>
            <div className="u0-kv"><span className="k">Second signer</span><span className="v">{step.signature_policy || (second ? "not set" : "—")}</span></div>
          </div>
        ) : null}
        {/* 2026-10-28 (owner) · what it records, the checklist, the parameter (and how it is monitored) and
            the fields the operator fills — optional, and in this section on every step type, as on an
            inspection. A recorded parameter and a charge kept the records and the fields behind a
            "Fields" / "Parameter & fields" chip. */}
        {details}
        {/* 2026-10-21 (owner) · a photo / file attachment, on any step but a clearance or a cleaning. */}
        {fieldShown("panel.attachment") ? <AttachmentField step={step} readOnly={readOnly} onChange={set} vocab={vocab} /> : null}
      </Section>
      ) : null}

      {shape.lead === "evidence" ? (
        <Section id="docs" title="The evidence — images and procedures" open={isOpen("docs")} onToggle={toggle}
                 badge={docsCount ? String(docsCount) : null}>
          {areaBody.docs}
        </Section>
      ) : null}

      {/* The occasional areas, as chips: a chip says its state; pressing it opens its area here. */}
      <div className="u0-chips" role="list">
        {chips.map((c) => {
          const st = chipState({ hasContent: c.has, required: c.required, readOnly });
          const on = isOpen(c.id);
          return (
            <button key={c.id} type="button" role="listitem" data-chip={c.id}
                    className={`u0-chip ${st} ${on ? "on" : ""}`} aria-expanded={on}
                    onClick={() => toggle(c.id)}>
              {st === "add" ? "+ " : ""}{c.label}
              {st === "missing" ? <span className="cv"> · required</span>
                : c.value ? <span className="cv"> · {c.value}</span> : null}
            </button>
          );
        })}
      </div>
      {chips.filter((c) => isOpen(c.id) && areaBody[c.id]).map((c) => (
        <Section key={c.id} id={c.id === "details" ? "details" : c.id} title={c.label} open onToggle={toggle} className="drawer"
                 help={areaHelp[c.id] || null}>
          {areaBody[c.id]}
        </Section>
      ))}
    </Flex>
    </HelpContext.Provider>
  );
}
